/* ============================================================
   EXON DROPS CLIENT — the SOLE caller of the drops API.

   Nothing else in the theme talks to that API. One place for the
   retry policy, the failure policy and the session record, because
   the traffic shape is ~8,000 people tapping the same Telegram link
   inside the same minute at 17:00 — a second caller with its own
   idea of "retry" is an outage, not a duplication.

   Wiring:
   · Config: #exon-drops-cfg (emitted by layout/theme.liquid) —
     {enabled, apiBase, couponApplicationMode}.
   · Output: window.EXON.drops (state + snapshot getter), an
     `exon:drops:state` CustomEvent, a bar-registry registration,
     and the removal of the [data-drops-pending] pre-paint flag.

   Contract (deployed; this file conforms, it does not negotiate):
     POST <apiBase>/session  body {"exd":"<token>"}   -> {ok, session, state}
     POST <apiBase>/state    body {}                  -> {ok, session, state}
                             header x-drops-session: <uuid>
     POST <apiBase>/weekend/claim  body {offer_key}   -> {ok, session, state}
     POST <apiBase>/weekend/select body {offer_key}   -> {ok, session, state}
     POST <apiBase>/gift/reserve                      -> {ok, session, state}
                             body {option_variant_gid, client_cart_total_agorot,
                                   client_cart_digest, request_id}
                             header x-drops-session: <uuid>
     POST <apiBase>/anchor/offer   body {cart, request_id}
     POST <apiBase>/anchor/pick    body {anchor_offer_id, option_id, cart,
                                         request_id}
     POST <apiBase>/mystery/deal   body {cart, request_id}
     POST <apiBase>/mystery/pick   body {mystery_attempt_id, slot, cart,
                                         expected_hand_digest, request_id}
     POST <apiBase>/choice/handoff body {choice_grant_id, report}
                             all five -> {ok, session, state, result}
                             header x-drops-session: <uuid>
     errors {ok:false, error:{code, retry_after_seconds}}

   The /session response ALREADY carries state, so a link arrival is
   ONE request, not two. Every successful state envelope is parsed by exactly one
   function (ingestState) — a second ingestion path would hand the
   halved request count straight back as parsing drift.

   Every boot/state-read failure renders as not-entitled. Gift mutations keep
   canonical entitlement and publish a separate local refused action state.
   ============================================================ */
(function () {
  'use strict';

  if (window.__exonDropsInit) return;
  window.__exonDropsInit = true;
  window.EXON = window.EXON || {};

  /* ---------------- faults ----------------
     IF IT CATCHES, IT COUNTS AND EMITS. A customer surface still shows
     nothing on any failure — that part is deliberate — but a silent catch
     makes "this customer has no drop" and "the drops client is broken" look
     identical from the outside, and only one of those is worth waking up for.
     Every fault stamps a stable name onto the root element and logs it. */
  function fault(name, detail) {
    try {
      var h = document.documentElement;
      var cur = h.getAttribute('data-drops-fault') || '';
      if ((' ' + cur + ' ').indexOf(' ' + name + ' ') === -1) {
        h.setAttribute('data-drops-fault', cur ? cur + ' ' + name : name);
      }
    } catch (e) { /* no document is not a fault we can report */ }
    try { console.error('[exon:drops] ' + name, detail || ''); } catch (e) {}
  }

  /* ---------------- config ---------------- */
  function readCfg() {
    var el = document.getElementById('exon-drops-cfg');
    /* absent is not a fault — the blob is only emitted where drops apply */
    if (!el || !el.textContent) return {};
    try {
      return JSON.parse(el.textContent) || {};
    } catch (e) {
      /* malformed config disables the ENTIRE client, so it can never be quiet */
      fault('config_parse_failed', e);
      return {};
    }
  }
  var CFG = readCfg();
  var API_BASE = String(CFG.apiBase == null ? '' : CFG.apiBase).trim().replace(/\/+$/, '');
  /* ABSENCE MEANS OFF. Only an explicit JSON boolean `true` may arm this rail.
     This was `CFG.enabled !== false`, which armed on ANY value that was not
     literally false -- undefined, null, the string "true" -- so the property
     merely had to stop being a boolean.

     TWO SCENARIOS THAT SOUND LIKE THE HAZARD AND ARE NOT, recorded because
     both were asserted during review and both are false: (a) losing
     `drops_enabled` from settings_data.json does NOT arm it -- theme.liquid
     applies `| default: false | json`, so the blob still carries a boolean
     false; (b) publishing does not move settings between themes at all --
     settings are per-theme, and a copy/duplicate/push is what carries them.

     What DOES reach it: a renamed or removed property in the emitting
     template while this reader still looks for `enabled`, a second writer of
     the blob, or a hand-edited config -- version skew, not settings loss.
     A malformed blob is safe under either form, because readCfg() returns {}
     and apiBase vanishes with it. So this is DEFENCE IN DEPTH against skew,
     not the closing of a live hole.

     Kept strict anyway: for an on-switch whose only job is to be off in
     production for ~8,000 members, arming must require a value that exists
     and says so. Found by exon-storefront; scenario corrected by codex
     review, 2026-08-09. Regression cases live in tests/t0-drops-client.test.ts. */
  var ENABLED = CFG.enabled === true && !!API_BASE;
  /* both arms are built; neither is selected here. The theme setting picks. */
  var APPLY_MODE = CFG.couponApplicationMode === 'discount_url' ? 'discount_url' : 'manual_code';

  var PENDING_ATTR = 'data-drops-pending';
  var STORAGE_KEY = 'exon:drops:session';
  var APPLIED_PREFIX = 'exon:drops:applied:';
  var FETCH_TIMEOUT_MS = 4500;
  var RETRY_DELAY_MS = 800;
  var RETRY_JITTER_MS = 400;
  var MAX_TOKEN_LEN = 4096;

  /* the session ref travels in a request HEADER. The contract says uuid; this
     is a deliberate superset that still cannot terminate or inject a header. */
  var REF_RE = /^[A-Za-z0-9._~-]{8,200}$/;

  /* ---------------- tiny helpers ---------------- */
  function str(v) { return (v == null || v === '') ? null : String(v); }
  function copy(o) {
    var out = {};
    for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) out[k] = o[k];
    return out;
  }
  function delay(ms) { return new Promise(function (r) { setTimeout(r, ms); }); }
  /* an ISO string (what the API sends) or an epoch in MILLISECONDS — the same
     unit the bar composer uses, and the unit we write our own record in. A
     seconds-vs-ms guess is not worth the ambiguity; `expires_in_seconds` is
     the one place seconds appear and it is converted where it is read. */
  function toMs(v) {
    if (v == null || v === '') return null;
    if (typeof v === 'number') return isFinite(v) ? v : null;
    var t = Date.parse(v);
    return isNaN(t) ? null : t;
  }

  /* ---------------- session record storage ----------------
     localStorage → sessionStorage → in-memory. Only the opaque ref and its
     expiry are ever persisted — never a code, never a customer identifier. */
  var Store = (function () {
    var mem = null;

    function backing() {
      var out = [];
      try { if (window.localStorage) out.push(window.localStorage); } catch (e) { /* blocked */ }
      try { if (window.sessionStorage) out.push(window.sessionStorage); } catch (e) { /* blocked */ }
      return out;
    }
    function read() {
      var stores = backing();
      for (var i = 0; i < stores.length; i++) {
        var raw = null;
        try { raw = stores[i].getItem(STORAGE_KEY); } catch (e) { raw = null; }
        var rec = parseRecord(raw);
        if (rec) return rec;
      }
      return parseRecord(mem);
    }
    function write(rec) {
      var raw = JSON.stringify({ ref: rec.ref, exp: rec.exp });
      var stores = backing();
      for (var i = 0; i < stores.length; i++) {
        try { stores[i].setItem(STORAGE_KEY, raw); return true; } catch (e) { /* full / private */ }
      }
      mem = raw; /* survives this page and nothing more */
      return false;
    }
    function clear() {
      var stores = backing();
      for (var i = 0; i < stores.length; i++) {
        try { stores[i].removeItem(STORAGE_KEY); } catch (e) { /* nothing to undo */ }
      }
      mem = null;
    }
    return { read: read, write: write, clear: clear };
  })();

  function parseRecord(raw) {
    if (!raw) return null;
    var rec = null;
    try { rec = JSON.parse(raw); } catch (e) { return null; }
    if (!rec || typeof rec !== 'object') return null;
    if (!REF_RE.test(String(rec.ref || ''))) return null;
    /* an expired local record is rejected BEFORE reuse — the server would
       refuse it anyway, and a doomed request at 17:00 is one we need not send */
    var exp = toMs(rec.exp);
    if (exp !== null && exp <= Date.now()) return null;
    return { ref: String(rec.ref), exp: exp };
  }

  /* The contract pins the request bodies and the error envelope, not the inner
     shape of `session`. Accept a bare ref or the common object spellings, and
     keep exactly one record shape on our side. */
  function normalizeSession(s) {
    if (!s) return null;
    var ref = null;
    var exp = null;
    if (typeof s === 'string') {
      ref = s;
    } else if (typeof s === 'object') {
      ref = s.ref || s.id || s.session_ref || s.session_id || s.token || null;
      exp = s.expires_at || s.expiresAt || s.expiry || null;
      if (exp == null && s.expires_in_seconds != null) {
        var secs = Number(s.expires_in_seconds);
        if (isFinite(secs)) exp = Date.now() + secs * 1000;
      }
    }
    if (!ref || !REF_RE.test(String(ref))) return null;
    return { ref: String(ref), exp: toMs(exp) };
  }

  /* ------------------------------------------------------------
     THE SINGLE STATE INGESTION POINT.
     /session and /state answer with the same envelope, so one function
     asserts the shape for both. When `entitled` is false the payload
     contains that key and NOTHING else — the server strips it — which is
     why no other field may be read before `entitled` is checked.
     ------------------------------------------------------------ */
  var NOT_ENTITLED = { entitled: false };

  function ingestState(body) {
    var raw = body && body.state;
    if (!raw || typeof raw !== 'object') return NOT_ENTITLED;
    if (raw.entitled !== true) return NOT_ENTITLED;
    var usage = raw.mechanic === 'launch_override'
      ? normalizeLaunchUsage(raw.usage) : raw.mechanic === 'weekend' && raw.usage &&
        /^weekend_(single_use|reusable)_v1$/.test(raw.usage.policy || '')
        ? normalizeWeekendUsage(raw.usage) : normalizeCouponUsage(raw.usage);
    if (usage === false) return NOT_ENTITLED;
    var p = raw.presentation; /* NESTED — {headline, highlight}, not flat */
    return {
      entitled: true,
      campaign_id: str(raw.campaign_id),
      campaign_version_id: str(raw.campaign_version_id),
      mechanic: str(raw.mechanic),
      status: str(raw.status),
      window_start_at: str(raw.window_start_at),
      window_end_at: str(raw.window_end_at),
      /* `highlight` keeps its RAW type. SQL emits it as a number and the
         renderer formats it; coercing it to a string here is what made the
         strip show a bare `30` instead of `-30%`. */
      presentation: (p && typeof p === 'object')
        ? { headline: str(p.headline), highlight: (p.highlight == null || p.highlight === '') ? null : p.highlight }
        : null,
      discount_redirect_path: str(raw.discount_redirect_path),
      code: str(raw.code),
      code_state: str(raw.code_state),
      code_expires_at: str(raw.code_expires_at),
      usage: usage,
      gift: normalizeGift(raw.gift),
      weekend_event: normalizeWeekendMenu(raw.weekend_event, raw),
      /* E3 CHOICE. `null` for every non-choice session, by the server's own
         construction (choice_member_state). Normalised in exactly one place,
         like every other block, and stripped of anything the projection is
         not supposed to be carrying — see normalizeChoice. */
      choice: normalizeChoice(raw.choice),
      /* THE TWO ACCEPTED FUTURE BLOCKS (storefront-state-contract.ts
         ACCEPTED_FUTURE_CHANGES: order_application, condition). Both are
         additive, nested and entitled-only; both are absent on the wire today
         and read as null. Nothing here invents either: a block is a block
         only when the server sent an object carrying its one number. */
      order_application: futureAmount(raw.order_application, 'amount_agorot'),
      condition: futureAmount(raw.condition, 'minimum_subtotal_agorot')
    };
  }

  function whole(v) {
    return (typeof v === 'number' && isFinite(v) && v >= 0 && Math.floor(v) === v) ? v : null;
  }
  function giftString(v) { return (typeof v === 'string' && v.length) ? v : null; }

  function normalizeCouponUsage(raw) {
    if (raw == null) return null;
    if (!raw || typeof raw !== 'object' || raw.policy !== 'shared_checkout_cap_v1' ||
        whole(raw.checkout_cap) === null || raw.checkout_cap < 1 ||
        raw.once_per_customer !== true || raw.reserves_a_place !== false ||
        !/^(intended|ready|closing|stopped)$/.test(raw.node_state || '') ||
        !/^(pending|ready|expired|refused)$/.test(raw.member_state || '') ||
        !giftString(raw.expires_at) || toMs(raw.expires_at) === null) return false;
    return {
      policy: 'shared_checkout_cap_v1',
      checkout_cap: raw.checkout_cap,
      once_per_customer: true,
      reserves_a_place: false,
      node_state: raw.node_state,
      member_state: raw.member_state,
      expires_at: raw.expires_at
    };
  }

  function normalizeWeekendMenu(menu, parent) {
    if (menu == null) return null;
    if (parent.mechanic !== 'weekend' || !menu || Array.isArray(menu) ||
        menu.campaign_id !== parent.campaign_id || menu.campaign_version_id !== parent.campaign_version_id ||
        !giftString(menu.parent_status) || menu.parent_status !== parent.status ||
        (menu.window_start_at !== null && toMs(menu.window_start_at) === null) ||
        (menu.window_end_at !== null && toMs(menu.window_end_at) === null) ||
        menu.window_start_at !== parent.window_start_at || menu.window_end_at !== parent.window_end_at ||
        typeof menu.acquisition_open !== 'boolean' ||
        (menu.selected_offer_key !== null && !/^[a-z0-9][a-z0-9_]{0,15}$/.test(menu.selected_offer_key || '')) ||
        !Array.isArray(menu.offers) ||
        !menu.offers.length || menu.offers.length > 20) return null;
    var keys = {}, offers = [], selected = 0;
    for (var i = 0; i < menu.offers.length; i++) {
      var o = menu.offers[i], u = o && o.native_usage, a = o && o.availability, h = o && o.held;
      if (!o || !/^[a-z0-9][a-z0-9_]{0,15}$/.test(o.offer_key || '') || keys[o.offer_key] ||
          !/^(percentage|gift)$/.test(o.kind || '') || toMs(o.ends_at) === null ||
          typeof o.selected !== 'boolean' || !u || typeof u.reusable !== 'boolean' ||
          typeof u.applies_once_per_customer !== 'boolean' || typeof u.reserves_a_place !== 'boolean' ||
          (u.usage_limit !== null && whole(u.usage_limit) === null) ||
          !a || typeof a.claimable !== 'boolean' || whole(a.remaining) === null || !o.conditions) return null;
      if (o.kind === 'percentage' && (!/^weekend_(single_use|reusable)_v1$/.test(u.policy || '') ||
          u.reusable !== (u.policy === 'weekend_reusable_v1') || u.reserves_a_place !== false ||
          u.applies_once_per_customer !== false ||
          u.usage_limit !== (u.reusable ? null : 1) || typeof u.pct !== 'number' ||
          !isFinite(u.pct) || u.pct <= 0 || u.pct > 100)) return null;
      if (o.kind === 'percentage') {
        if (o.conditions.scope_kind === 'all') {
          // No identifier is needed: the provider node itself carries all-items scope.
        } else if (o.conditions.scope_kind === 'collections') {
          if (!Array.isArray(o.conditions.scope_collection_gids) ||
              !o.conditions.scope_collection_gids.length ||
              o.conditions.scope_collection_gids.some(function (v) {
                return !/^gid:\/\/shopify\/Collection\/\d+$/.test(v || '');
              })) return null;
        } else if (o.conditions.scope_kind === 'variants') {
          if (!Array.isArray(o.conditions.scope_variant_gids) ||
              !o.conditions.scope_variant_gids.length ||
              o.conditions.scope_variant_gids.some(function (v) {
                return !/^gid:\/\/shopify\/ProductVariant\/\d+$/.test(v || '');
              })) return null;
        } else return null;
      }
      if (o.kind === 'gift' && (u.policy !== 'weekend_gift_bxgy_v1' || u.reusable ||
          u.pct !== null || u.usage_limit !== 1 || u.applies_once_per_customer !== true ||
          u.reserves_a_place !== true || o.conditions.scope_kind !== 'collections' ||
          !/^gid:\/\/shopify\/Collection\/\d+$/.test(o.conditions.collection_gid || '') ||
          !giftString(o.conditions.handle) || !/^[0-9a-f]{64}$/i.test(o.conditions.rules_sha256 || '') ||
          !Array.isArray(o.conditions.options) || !o.conditions.options.length)) return null;
      if (o.kind === 'gift') for (var n = 0; n < o.conditions.options.length; n++) {
        var go = o.conditions.options[n];
        if (!go || go.ordinal !== n || !/^gid:\/\/shopify\/ProductVariant\/\d+$/.test(go.variant_gid || '') ||
            !giftString(go.title) || whole(go.threshold_agorot) === null ||
            whole(go.minimum_subtotal_agorot) === null || whole(go.cap) === null ||
            whole(go.remaining) === null || typeof go.available !== 'boolean') return null;
      }
      if (h && (!giftString(h.reward_issue_id) || !/^(personal_code|gift_pass)$/.test(h.kind || '') ||
          !/^(pending|ready|expired|refused)$/.test(h.state || '') || typeof h.bound !== 'boolean' ||
          (h.expires_at !== null && toMs(h.expires_at) === null) ||
          (o.kind === 'percentage' && h.kind !== 'personal_code') ||
          (o.kind === 'gift' && h.kind !== 'gift_pass'))) return null;
      if (o.selected) {
        selected++;
        if (!h || o.offer_key !== menu.selected_offer_key) return null;
      }
      // The server alone selects a credential. Never retain an unselected code.
      var clean = copy(o);
      var credential = o.credential;
      if (credential != null && (!o.selected || !h || h.kind !== 'personal_code' || h.state !== 'ready' ||
          !giftString(credential.code) || !giftString(credential.code_state) ||
          (credential.expires_at !== null && toMs(credential.expires_at) === null))) return null;
      clean.credential = credential ? copy(credential) : null;
      keys[o.offer_key] = true;
      offers.push(clean);
    }
    if (selected > 1 || (menu.selected_offer_key !== null && selected !== 1) ||
        (menu.selected_offer_key === null && selected !== 0)) return null;
    return { campaign_id: menu.campaign_id, campaign_version_id: menu.campaign_version_id,
      parent_status: menu.parent_status, window_start_at: menu.window_start_at,
      window_end_at: menu.window_end_at, acquisition_open: menu.acquisition_open,
      selected_offer_key: menu.selected_offer_key, offers: offers };
  }

  function isWeekendUsage(u) {
    return !!(u && /^weekend_(single_use|reusable)_v1$/.test(u.policy || ''));
  }
  function normalizeWeekendUsage(raw) {
    var reusable = raw.policy === 'weekend_reusable_v1';
    var observed = raw.observed;
    if (!/^[a-z0-9][a-z0-9_]{0,15}$/.test(raw.offer_key || '') ||
        typeof raw.pct !== 'number' || !isFinite(raw.pct) || raw.pct <= 0 || raw.pct > 100 ||
        raw.reusable !== reusable || raw.reserves_a_place !== false ||
        !/^(pending|ready|expired|refused)$/.test(raw.member_state || '') ||
        raw.still_usable !== (raw.member_state === 'ready') ||
        !giftString(raw.expires_at) || toMs(raw.expires_at) === null ||
        !observed || !Number.isSafeInteger(observed.applications) || observed.applications < 0 ||
        !Number.isSafeInteger(observed.total_amount_agorot) || observed.total_amount_agorot < 0 ||
        (observed.last_observed_at !== null && (!giftString(observed.last_observed_at) ||
          toMs(observed.last_observed_at) === null))) return false;
    return {
      policy: raw.policy, offer_key: raw.offer_key, pct: raw.pct, reusable: reusable,
      reserves_a_place: false, member_state: raw.member_state,
      still_usable: raw.still_usable, expires_at: raw.expires_at,
      observed: { applications: observed.applications, total_amount_agorot: observed.total_amount_agorot,
        last_observed_at: observed.last_observed_at }
    };
  }
  function usageReady(u) {
    return u && u.member_state === 'ready' && (isWeekendUsage(u)
      ? u.still_usable === true : /^(ready|closing)$/.test(u.node_state || ''));
  }

  function normalizeLaunchUsage(raw) {
    if (raw == null) return null;
    var uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    var gid = /^gid:\/\/shopify\/ProductVariant\/\d+$/;
    if (!raw || typeof raw !== 'object' || raw.policy !== 'launch_bxgy_v1' ||
        !uuid.test(raw.reward_issue_id || '') || !gid.test(raw.gift_variant_gid || '') ||
        raw.buy_quantity !== 1 || raw.gift_quantity !== 1 ||
        (raw.use_cap !== null && (whole(raw.use_cap) === null || raw.use_cap < 1)) ||
        raw.once_per_customer !== true || raw.reserves_a_place !== false ||
        !/^(intended|ready|closing|stopped)$/.test(raw.node_state || '') ||
        !/^(pending|ready|expired|refused)$/.test(raw.member_state || '') ||
        !giftString(raw.expires_at) || toMs(raw.expires_at) === null) return false;
    var variants = raw.subject_variant_gids;
    var collection = raw.subject_collection_gid;
    if (raw.subject_kind === 'variants') {
      if (!Array.isArray(variants) || !variants.length ||
          variants.some(function (v) { return typeof v !== 'string' || !gid.test(v); }) || collection != null) return false;
    } else if (raw.subject_kind === 'collection') {
      if (!/^gid:\/\/shopify\/Collection\/\d+$/.test(collection || '') ||
          (variants != null && (!Array.isArray(variants) || variants.length))) return false;
    } else return false;
    return {
      policy: 'launch_bxgy_v1', reward_issue_id: raw.reward_issue_id,
      gift_variant_gid: raw.gift_variant_gid, gift_title: giftString(raw.gift_title),
      subject_kind: raw.subject_kind,
      subject_variant_gids: raw.subject_kind === 'variants' ? variants.slice() : [],
      subject_collection_gid: raw.subject_kind === 'collection' ? collection : null,
      buy_quantity: 1, gift_quantity: 1, use_cap: raw.use_cap,
      once_per_customer: true, reserves_a_place: false,
      node_state: raw.node_state, member_state: raw.member_state, expires_at: raw.expires_at
    };
  }

  /* STAGE 3 GIFT GOAL — additive and nullable. Both legacy amounts are kept:
     `advertised_agorot` mirrors threshold_agorot (the promise copy) and
     `enforced_agorot` mirrors minimum_subtotal_agorot (the provider term).
     Anything that does not mirror its option exactly, or breaks the legacy
     `enforced >= advertised` rule, is not the block: null, never a zero. The
     scope only names the collection route whose server-rendered membership
     fragment drops-membership.js reads and joins; no browser code guesses
     membership from the handle, a product's collections or the cart total. */
  function normalizeGiftGoal(raw, option) {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
    var advertised = whole(raw.advertised_agorot);
    var enforced = whole(raw.enforced_agorot);
    if (raw.basis !== 'collection_scope' || advertised === null || enforced === null ||
        enforced < advertised || advertised !== option.threshold_agorot ||
        enforced !== option.minimum_subtotal_agorot) return null;
    var scope = null;
    if (raw.scope !== null && raw.scope !== undefined) {
      var sc = raw.scope;
      if (!sc || typeof sc !== 'object' || Array.isArray(sc) ||
          !/^gid:\/\/shopify\/Collection\/\d+$/.test(sc.collection_gid || '') ||
          !giftString(sc.handle) || !/^[0-9a-f]{64}$/i.test(sc.rules_sha256 || '')) return null;
      scope = { collection_gid: sc.collection_gid, handle: sc.handle, rules_sha256: sc.rules_sha256 };
    }
    return { basis: 'collection_scope', advertised_agorot: advertised, enforced_agorot: enforced, scope: scope };
  }
  /* The server's selected option ordinal, or null. ABSENT (an older server)
     is null: no selection is inferred from `held` or anything else. A value
     that names no option, or disagrees with the held entitlement, is null. */
  function giftSelectedOption(raw, options, held) {
    var n = whole(raw);
    if (n === null || !options[n]) return null;
    if (held && held.option_ordinal !== n) return null;
    return n;
  }

  function normalizeGift(raw) {
    if (!raw || typeof raw !== 'object') return null;
    if (!Array.isArray(raw.options)) return null;
    if (typeof raw.closed !== 'boolean' || !giftString(raw.cutoff_at) || toMs(raw.cutoff_at) === null) return null;
    var options = [];
    for (var i = 0; i < raw.options.length; i++) {
      var o = raw.options[i];
      if (!o || typeof o !== 'object') return null;
      var ordinal = whole(o.option_ordinal);
      var gid = giftString(o.variant_gid);
      var title = giftString(o.title);
      if (ordinal === null || ordinal !== i || !gid || !/^gid:\/\/shopify\/ProductVariant\/\d+$/.test(gid) || !title || typeof o.available !== 'boolean') return null;
      var option = {
        option_ordinal: ordinal,
        variant_gid: gid,
        title: title,
        cap: whole(o.cap),
        issued: whole(o.issued),
        remaining: whole(o.remaining),
        threshold_agorot: whole(o.threshold_agorot),
        minimum_subtotal_agorot: whole(o.minimum_subtotal_agorot),
        pool_state: giftString(o.pool_state),
        available: o.available === true
      };
      if (option.cap === null || option.issued === null || option.remaining === null ||
          option.threshold_agorot === null || option.minimum_subtotal_agorot === null ||
          !option.pool_state) return null;
      option.goal = normalizeGiftGoal(o.goal, option);
      options.push(option);
    }
    var held = null;
    if (raw.held && typeof raw.held === 'object') {
      held = {
        entitlement_id: giftString(raw.held.entitlement_id),
        option_ordinal: whole(raw.held.option_ordinal),
        variant_gid: giftString(raw.held.variant_gid),
        code: giftString(raw.held.code),
        state: giftString(raw.held.state),
        expires_at: giftString(raw.held.expires_at),
        reserved_at: giftString(raw.held.reserved_at)
      };
      var heldOption = held.option_ordinal === null ? null : options[held.option_ordinal];
      if (!held.entitlement_id || !heldOption || held.variant_gid !== heldOption.variant_gid || !held.code ||
          !held.expires_at || !held.reserved_at || !/^(reserved|consumed|expired)$/.test(held.state || '')) return null;
    }
    var actions = raw.actions;
    if (!actions || typeof actions !== 'object' || typeof actions.reserve_allowed !== 'boolean' ||
        typeof actions.cart_attach_allowed !== 'boolean') return null;
    var out = {
      cutoff_at: raw.cutoff_at,
      closed: raw.closed,
      options: options,
      actions: {
        reserve_allowed: actions.reserve_allowed === true,
        cart_attach_allowed: actions.cart_attach_allowed === true
      },
      held: held,
      selected_option: giftSelectedOption(raw.selected_option, options, held),
      cap: whole(raw.cap),
      issued: whole(raw.issued),
      remaining: whole(raw.remaining),
      threshold_agorot: whole(raw.threshold_agorot),
      minimum_subtotal_agorot: whole(raw.minimum_subtotal_agorot),
      variant_gid: giftString(raw.variant_gid),
      pool_state: giftString(raw.pool_state)
    };
    var singletonKeys = ['cap', 'issued', 'remaining', 'threshold_agorot', 'minimum_subtotal_agorot', 'variant_gid', 'pool_state'];
    if (options.length === 1) {
      for (var j = 0; j < singletonKeys.length; j++) {
        var key = singletonKeys[j];
        if (out[key] !== options[0][key]) return null;
      }
    } else {
      for (var k = 0; k < singletonKeys.length; k++) if (raw[singletonKeys[k]] !== null) return null;
    }
    return out;
  }

  /* ============================================================
     THE E3 CHOICE BLOCK — normalised once, here, like every other block.

     Source of truth: drops_private.choice_member_state (migration
     20261002006730, extended by 006740) as projected through
     supabase/functions/_shared/drops/storefront-state-contract.ts:

       choice = { public, offer, attempt, last_attempt, grants }

     THREE RULES THIS FUNCTION ENFORCES, and none of them is cosmetic:

     1. THE SEALED MAP IS NEVER CARRIED. `attempt` gets a slot COUNT and a
        hand digest and nothing else about the hand; `advertised` is the
        campaign's public prize SET, identical for every member. There is no
        slot-to-prize field on the wire and this function has no place to put
        one if a later projection grew it — a card's identity arrives ONLY in
        a pick result the server sent back.
     2. `holdout` IS DROPPED IF IT EVER ARRIVES. The state projection omits
        it deliberately; the anchor_offer ROUTE RESULT still carries it
        (commit_anchor_offer's return), so the key is scrubbed at both
        entrances. A member who is told they are in the measurement holdout
        behaves differently, which is the one thing a holdout cannot survive.
     3. `code_available` AND `code` ARE TWO FACTS. Readiness is not
        disclosure: a grant may be issued and bound while `code` stays null
        because this member is no longer the customer it was pinned to. They
        are carried separately and nothing here derives one from the other.
     ============================================================ */
  function choiceString(v) { return (typeof v === 'string' && v !== '') ? v : null; }
  function nonNegInt(v) {
    return (typeof v === 'number' && isFinite(v) && v >= 0 && Math.floor(v) === v) ? v : null;
  }
  function plainObject(v) {
    return (v && typeof v === 'object' && !Array.isArray(v)) ? v : null;
  }
  /* the server's own public prize/option descriptors, copied field by field.
     An unknown key is DROPPED rather than carried: this list is what the
     surfaces are allowed to draw, and a projection that grows a field nobody
     reviewed must not reach a card face by default. */
  var DISPLAY_KEYS = ['title', 'name', 'sub', 'subtitle', 'art_url', 'accent', 'variant_gid', 'option_id'];
  function displayRecord(raw) {
    var o = plainObject(raw);
    if (!o) return null;
    var out = {};
    for (var i = 0; i < DISPLAY_KEYS.length; i++) {
      var k = DISPLAY_KEYS[i];
      if (typeof o[k] === 'string' && o[k] !== '') out[k] = o[k];
    }
    return out;
  }
  function displayList(raw) {
    if (!Array.isArray(raw)) return [];
    var out = [];
    for (var i = 0; i < raw.length; i++) {
      var rec = displayRecord(raw[i]);
      if (rec) out.push(rec);
    }
    return out;
  }

  function normalizeAnchorOffer(raw) {
    var o = plainObject(raw);
    if (!o) return null;
    var id = choiceString(o.anchor_offer_id);
    var expires = choiceString(o.expires_at);
    var digest = choiceString(o.reported_cart_digest);
    if (!id || !expires || !digest) return null;
    /* EVERY OPTION MUST BE IDENTIFIABLE. `option_id` is what a pick submits;
       an option without one is an unpickable bay and the whole offer is
       refused rather than shown short — an offer promising three choices with
       two that work is worse than no offer. */
    var options = [];
    var seen = Object.create(null);
    if (!Array.isArray(o.options)) return null;
    for (var i = 0; i < o.options.length; i++) {
      var rec = displayRecord(o.options[i]);
      var oid = rec ? rec.option_id : null;
      if (!oid || oid !== oid.trim() || oid.length > 200) return null;
      if (seen[oid]) return null;
      seen[oid] = true;
      options.push(rec);
    }
    var count = nonNegInt(o.option_count);
    /* THE COUNT AND THE LIST DESCRIBE THE SAME OFFER. A mismatch means one of
       them is wrong and the client cannot tell which; the approved copy says
       one/two/three truthfully, so a disagreement is refused. */
    if (count === null || count !== options.length) return null;
    if (options.length < 1 || options.length > 3) return null;
    return {
      anchor_offer_id: id,
      options: options,
      option_count: options.length,
      expires_at: expires,
      reported_cart_digest: digest
    };
  }

  function normalizeMysteryAttempt(raw) {
    var o = plainObject(raw);
    if (!o) return null;
    var id = choiceString(o.mystery_attempt_id);
    var digest = choiceString(o.hand_digest);
    var expires = choiceString(o.hand_expires_at);
    var slots = nonNegInt(o.slot_count);
    var no = nonNegInt(o.attempt_no);
    if (!id || !digest || !expires || slots === null || no === null) return null;
    if (o.state !== 'dealt') return null;
    /* The executed server contract deals 1..64 cards. One is the conserved
       deck tail, and slot 0 of that hand must remain playable. */
    if (slots < 1 || slots > 64) return null;
    return {
      mystery_attempt_id: id,
      attempt_no: no,
      state: 'dealt',
      slot_count: slots,
      hand_digest: digest,
      hand_expires_at: expires,
      advertised: displayList(o.advertised)
    };
  }

  var LAST_ATTEMPT_OUTCOME_RE = /^(win|blank|expired)$/;
  function normalizeLastAttempt(raw) {
    var o = plainObject(raw);
    if (!o) return null;
    var id = choiceString(o.mystery_attempt_id);
    var outcome = choiceString(o.outcome);
    var no = nonNegInt(o.attempt_no);
    if (!id || !outcome || no === null || !LAST_ATTEMPT_OUTCOME_RE.test(outcome)) return null;
    return {
      mystery_attempt_id: id,
      attempt_no: no,
      state: choiceString(o.state),
      outcome: outcome,
      slot: nonNegInt(o.slot),
      choice_grant_id: choiceString(o.choice_grant_id),
      picked_at: choiceString(o.picked_at)
    };
  }

  var GRANT_MECHANIC_RE = /^(anchor_reward|mystery_cards)$/;
  function normalizeGrants(raw) {
    if (!Array.isArray(raw)) return [];
    var out = [];
    var seen = Object.create(null);
    for (var i = 0; i < raw.length; i++) {
      var g = plainObject(raw[i]);
      if (!g) continue;
      var id = choiceString(g.choice_grant_id);
      var mechanic = choiceString(g.mechanic);
      var state = choiceString(g.state);
      if (!id || !mechanic || !state || !GRANT_MECHANIC_RE.test(mechanic)) continue;
      if (seen[id]) continue;
      seen[id] = true;
      out.push({
        choice_grant_id: id,
        mechanic: mechanic,
        state: state,
        variant_gid: choiceString(g.variant_gid),
        display: displayRecord(g.display),
        original_conditions: plainObject(g.original_conditions) || {},
        conditions_hash: choiceString(g.conditions_hash),
        ends_at: choiceString(g.ends_at),
        /* NO EXPIRY IS THE SERVER'S FACT, not "ends_at happened to be null" as
           read by a client that may have dropped the field. Both are carried;
           the surfaces read no_expiry. */
        no_expiry: g.no_expiry === true,
        code_available: g.code_available === true,
        code: choiceString(g.code),
        discount_gid: choiceString(g.discount_gid)
      });
    }
    return out;
  }

  /* {basis:'qualifying_variants', target_agorot, qualifying_variant_gids,
     minimum_quantity} — every field the server's own frozen bytes. A field
     that is malformed is null, never zero: a null minimum_quantity means the
     quantity is UNKNOWN and the rail stays amount-only, never "satisfied". */
  var CHOICE_GOAL_GID_RE = /^gid:\/\/shopify\/ProductVariant\/[0-9]+$/;
  function normalizeChoiceGoal(raw) {
    var g = plainObject(raw);
    if (!g || g.basis !== 'qualifying_variants') return null;
    var gids = null;
    if (Array.isArray(g.qualifying_variant_gids)) {
      var seen = Object.create(null);
      gids = [];
      for (var i = 0; i < g.qualifying_variant_gids.length; i++) {
        var v = g.qualifying_variant_gids[i];
        if (typeof v !== 'string' || !CHOICE_GOAL_GID_RE.test(v) || seen[v]) { gids = null; break; }
        seen[v] = true;
        gids.push(v);
      }
    }
    var q = nonNegInt(g.minimum_quantity);
    return {
      basis: 'qualifying_variants',
      target_agorot: nonNegInt(g.target_agorot),
      qualifying_variant_gids: gids,
      minimum_quantity: (q !== null && q >= 1 && q <= 100) ? q : null
    };
  }

  function normalizeChoice(raw) {
    var o = plainObject(raw);
    if (!o) return null;
    var pub = plainObject(o.public);
    var publicOut = null;
    if (pub) {
      publicOut = copy(pub);
      /* scrubbed at every entrance — see rule 2 above */
      delete publicOut.holdout;
      /* STAGE 3 CHOICE GOAL — carried only in its validated shape. An absent
         key stays absent (an older server); a malformed block is null. */
      if (Object.prototype.hasOwnProperty.call(pub, 'goal')) publicOut.goal = normalizeChoiceGoal(pub.goal);
    }
    return {
      public: publicOut,
      offer: normalizeAnchorOffer(o.offer),
      attempt: normalizeMysteryAttempt(o.attempt),
      last_attempt: normalizeLastAttempt(o.last_attempt),
      grants: normalizeGrants(o.grants)
    };
  }

  /* `null | {<key>: nonnegative integer}` — anything else is not the block */
  function futureAmount(block, key) {
    if (!block || typeof block !== 'object') return null;
    var n = block[key];
    if (typeof n !== 'number' || !isFinite(n) || n < 0 || Math.floor(n) !== n) return null;
    var out = {};
    out[key] = n;
    return out;
  }

  /* ---------------- transport ---------------- */
  function apiError(body, status) {
    var e = body && body.error;
    var err = new Error('drops_api_error');
    err.kind = 'api';
    err.code = (e && e.code) ? String(e.code) : ('http_' + status);
    err.retryAfterSeconds = (e && Number(e.retry_after_seconds)) || null;
    err.status = status;
    return err;
  }
  function netError() {
    var err = new Error('drops_network_error');
    err.kind = 'network';
    err.code = 'network_error';
    return err;
  }

  var WRITE_PATH_RE = /^\/(weekend\/(claim|select)|gift\/reserve|anchor\/offer|anchor\/pick|mystery\/deal|mystery\/pick)$/;

  function post(path, bodyObj, sessionRef) {
    var ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var timer = ctrl ? setTimeout(function () { try { ctrl.abort(); } catch (e) {} }, FETCH_TIMEOUT_MS) : null;
    var headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
    if (sessionRef) headers['x-drops-session'] = sessionRef;

    return fetch(API_BASE + path, {
      method: 'POST',
      headers: headers,
      signal: ctrl ? ctrl.signal : undefined,
      body: JSON.stringify(bodyObj)
    }).then(function (res) {
      if (timer) clearTimeout(timer);
      return res.json().catch(function () { return null; }).then(function (body) {
        if (res.ok && body && body.ok === true) return body;
        /* an HTTP answer IS an answer and must never reach the retry path:
           rate_limited and storefront_unavailable are exactly the two a
           client must not amplify. */
        var error = apiError(body, res.status);
        /* AN HTTP FAILURE WITHOUT A CODED REFUSAL IS AN UNKNOWN OUTCOME, on
           every route that can move a benefit. The server may have recorded
           the write and failed on the way back; the only safe next move is
           ONE state reconciliation, never a second attempt. The gift path
           established this; the four economic choice routes join it for the
           same reason. `/choice/handoff` is a REPORT, not a benefit move, so
           it is deliberately absent. */
        if (WRITE_PATH_RE.test(path) && !(body && body.ok === false &&
            body.error && typeof body.error.code === 'string' && body.error.code)) {
          error.outcomeUnknown = true;
        }
        /* `outcome_unknown` IS coded, but it is the server saying exactly the
           above in words: the write may have committed and the read-back
           failed. It takes the same single reconciliation, never a re-post. */
        if (WRITE_PATH_RE.test(path) && error.code === 'outcome_unknown') {
          error.outcomeUnknown = true;
        }
        throw error;
      });
    }, function () {
      if (timer) clearTimeout(timer);
      throw netError();
    });
  }

  /* HERD SAFETY, not a preference: one retry, network-level only, after
     800ms + 0-400ms of jitter so the retries of a synchronised crowd do not
     arrive as a second synchronised crowd. */
  function postOnce(path, body, ref) {
    return post(path, body, ref).catch(function (err) {
      if (err.kind !== 'network') throw err;
      return delay(RETRY_DELAY_MS + Math.random() * RETRY_JITTER_MS).then(function () {
        return post(path, body, ref);
      });
    });
  }

  /* ---------------- the ?exd= token ---------------- */
  function readExd() {
    try {
      var m = /[?&]exd=([^&#]*)/.exec(location.search || '');
      if (!m) return '';
      var v = '';
      try { v = decodeURIComponent(m[1].replace(/\+/g, ' ')); } catch (e) { v = m[1]; }
      v = v.trim();
      /* opaque to us — the server owns format, signature and expiry. The only
         local judgement is a length bound, so a junk URL cannot become a
         megabyte POST body. */
      return (v.length > 0 && v.length <= MAX_TOKEN_LEN) ? v : '';
    } catch (e) { return ''; }
  }

  /* ---------------- the ?drops_preview= token ----------------
     Forwarded to /public and to nothing else. Opaque here exactly as ?exd=
     is: the server is the security boundary and decides what a preview may
     see; the theme decides nothing, strips nothing and persists nothing. */
  function readPreview() {
    try {
      var m = /[?&]drops_preview=([^&#]*)/.exec(location.search || '');
      if (!m) return '';
      var v = '';
      try { v = decodeURIComponent(m[1].replace(/\+/g, ' ')); } catch (e) { v = m[1]; }
      v = v.trim();
      return (v.length > 0 && v.length <= MAX_TOKEN_LEN) ? v : '';
    } catch (e) { return ''; }
  }

  function stripExd() {
    try {
      if (!window.history || !history.replaceState) return;
      var url = new URL(location.href);
      if (!url.searchParams.has('exd')) return;
      url.searchParams.delete('exd');
      var q = url.searchParams.toString();
      history.replaceState(history.state, '', url.pathname + (q ? '?' + q : '') + url.hash);
    } catch (e) { /* a token left in the URL is cosmetic, never correctness */ }
  }

  /* ---------------- bar registry (pre-boot queue) ----------------
     The island may not have booted yet. Sources push operations; the island
     drains the queue when it installs. Load order therefore cannot matter. */
  function barOp(op, arg) {
    var b = (window.EXON.bar = window.EXON.bar || { q: [] });
    if (typeof b[op] === 'function') {
      try {
        b[op](arg);
      } catch (e) {
        /* the customer still sees no error — but a throwing registry means the
           entitled item never reached the strip, and `finish` clears the
           pending flag either way, so without this the whole rendering path
           being dead is indistinguishable from an ambient-only visitor. */
        fault('bar_' + op + '_failed', e);
      }
      return;
    }
    if (!b.q) b.q = [];
    b.q.push([op, arg]);
  }

  /* code_state is the server's enum. `open` is a code the client may apply;
     `redeemed` can now accompany the authenticated server application receipt
     and must be just as terminal for navigation as the other dead states. */
  var CODE_DEAD_RE = /^(redeemed|expired|revoked|consumed|invalid|void)$/;

  /* the discount is ON THE ORDER — the server's order_application record
     (ACCEPTED_FUTURE_CHANGES). Absent on the wire today, so the applied bar
     is unreachable until the Drop engine sends the block; nothing infers it. */
  function orderApplied(s) {
    if (isWeekendUsage(s.usage) && s.usage.reusable) return false;
    var o = s.order_application;
    return !!(o && typeof o === 'object' && typeof o.amount_agorot === 'number');
  }
  function codeLive(s) {
    if (!s.code) return false;
    if (CODE_DEAD_RE.test(s.code_state || '')) return false;
    var usage = s.usage;
    if (usage && !usageReady(usage)) return false;
    var exp = toMs(usage ? usage.expires_at : s.code_expires_at);
    return !(exp !== null && exp <= Date.now());
  }

  /* SQL emits `highlight` as a NUMBER and leaves the formatting here. Rendered
     as a plain ASCII hyphen, the digits, then the percent sign — never an en
     dash, an em dash or a Hebrew maqaf, which is a standing copy rule. The
     chip carries `.ltr`, so this LTR run stays intact inside the Hebrew line.
     A value outside 0-100 is not a percentage, so it is left as written. */
  function formatHighlight(v) {
    if (v == null || v === '') return '';
    var n;
    if (typeof v === 'number') {
      n = v;
    } else {
      var s = String(v).trim();
      /* a bare numeric string is the same payload as the number. Anything
         carrying formatting of its own is already authored copy and passes
         through untouched. */
      if (!/^-?\d+(\.\d+)?$/.test(s)) return String(v);
      n = Number(s);
    }
    if (!isFinite(n) || n === 0) return '';
    var pct = Math.abs(n);
    if (pct > 100) return String(v); /* not a percentage — a fixed amount, say */
    return '-' + (Math.round(pct * 100) / 100).toString() + '%';
  }

  /* ------------------------------------------------------------
     THE ADAPTER PRESETS. The bar contract knows only an ordered element list;
     what used to be an item `kind` lives here, in the one place that turns a
     server state into a bar item. Changing what a drop shows is a change to
     these three lists and to nothing else.

       countdown_offer  highlight · headline · timer [· code]
       applied          highlight · headline [· code]
       message          headline [· code]

     The member's item LEADS WITH THE STAMP, the same poster order the public
     Drop Coupon item uses - the figure first, then the words.

     `applied` is selected by the server's order_application block and by
     nothing else (spec §1 "Applied": the strip says the discount is on the
     order, which is a claim about money and needs the server's record of
     it). A code merely in hand is a RUNNING offer, so it rides the
     countdown_offer preset — the strip keeps its clock and its LIVE pip.

     The code element is not something the STRIP draws (the spec's bar has no
     code chip). It rides the item for the ticket, which is where the customer
     copies it from, and it stays an element rather than a fact so the bar's
     source lock (session items only) keeps guarding it.

     An element whose payload is empty is not emitted at all — the bar treats
     a required element with no content as a reason to skip the whole item,
     and rightly so.
     ------------------------------------------------------------ */
  function elementsFor(preset, p, code, hasWindowEnd) {
    var out = [];
    if (preset === 'applied' || preset === 'countdown_offer') {
      if (p.highlight) out.push({ type: 'highlight', pct: p.highlight });
    }
    if (p.headline) out.push({ type: 'headline', text: p.headline });
    /* the clock is dropped only once the discount is on the order */
    if (preset === 'countdown_offer' && hasWindowEnd) out.push({ type: 'timer' });
    if (code) out.push({ type: 'code', value: code });
    return out;
  }

  function toBarItem(s) {
    if (!s.entitled || !s.campaign_id) return null;
    var raw = s.presentation || {};
    var p = { headline: raw.headline, highlight: formatHighlight(isWeekendUsage(s.usage) ? s.usage.pct : raw.highlight) };

    /* the manual_code arm has to SHOW the issued code; the discount_url arm
       applies it for the customer and never displays one */
    var liveCode = codeLive(s);
    // A reusable offer keeps a copyable code even after its one-shot redirect.
    // A later cart may no longer carry the discount from an earlier order.
    var reusableCode = isWeekendUsage(s.usage) && s.usage.reusable;
    var code = ((APPLY_MODE === 'manual_code' || reusableCode) && liveCode && s.mechanic !== 'launch_override') ? s.code : null;
    /* Admission belongs to this member, independently of the shared node.
       Pending members retain a truthful ticket without code or navigation. */
    if (s.usage && !orderApplied(s)) {
      if (/^(expired|refused)$/.test(s.usage.member_state) ||
          toMs(s.usage.expires_at) <= Date.now()) return null;
      if (usageReady(s.usage) && !liveCode) return null;
    }

    var preset = 'message';
    if (orderApplied(s)) preset = 'applied';
    else if (s.window_end_at) preset = 'countdown_offer';

    var effectiveEnd = s.window_end_at;
    if (!orderApplied(s) && s.usage) {
      effectiveEnd = s.usage.expires_at;
    }
    var elements = elementsFor(preset, p, code, !!effectiveEnd);
    if (!elements.length) return null; /* nothing to render, so nothing to claim */

    /* built from the FORMATTED highlight, never the raw number — a screen
       reader announcing "30" where the strip reads "-30%" is its own defect */
    var label = [p.highlight, p.headline];
    var a11y = '';
    for (var i = 0; i < label.length; i++) if (label[i]) a11y += (a11y ? ' ' : '') + label[i];

    return {
      id: 'drops:' + s.campaign_id,
      source: 'session',
      trigger: s.campaign_id,
      band: 'entitled',
      elements: elements,
      a11y_label: a11y,
      window: (s.window_start_at || effectiveEnd)
        ? { start_at: s.window_start_at, end_at: effectiveEnd }
        : null,
      /* FACTS ride the item beside the elements, never inside them: the
         element vocabulary is closed and these are not things the strip
         draws. The ticket reads them (locked / redeemed), and the bar island
         keys its expired memory on the version id. Every value is a plain
         number or null — presentation-only on the client, per the contract's
         client_totals_advisory law; Shopify enforces at checkout. */
      facts: {
        version_id: s.campaign_version_id || null,
        applied_amount_agorot: orderApplied(s) ? s.order_application.amount_agorot : null,
        condition_min_agorot: s.condition ? s.condition.minimum_subtotal_agorot : null,
        shared_coupon: s.usage && s.usage.policy === 'shared_checkout_cap_v1' ? copy(s.usage) : null,
        weekend_offer: isWeekendUsage(s.usage) ? copy(s.usage) : null
      }
    };
  }

  /* ---------------- the discount_url arm ----------------
     One shot, marked BEFORE we navigate, so the return trip through the
     redirect target cannot bounce again. Relative paths only: /discount/… is
     same-origin by construction and the redirect target is refused unless it
     is a site-root path. */
  function markerSet(key) {
    try { window.sessionStorage.setItem(key, '1'); return true; } catch (e) { /* fall through */ }
    try { window.localStorage.setItem(key, '1'); return true; } catch (e) { /* fall through */ }
    return false;
  }
  function markerGet(key) {
    try { if (window.sessionStorage.getItem(key)) return true; } catch (e) { /* fall through */ }
    try { if (window.localStorage.getItem(key)) return true; } catch (e) { /* fall through */ }
    return false;
  }
  function relPath(p) {
    if (!p) return '';
    var v = String(p);
    if (/[\s\u0000-\u001f\u007f]/.test(v)) return '';
    if (v === '/') return v;
    return /^\/[^/\\]/.test(v) ? v : ''; /* never //host, never /\host, never absolute */
  }

  function maybeRedirect(s) {
    if (APPLY_MODE !== 'discount_url' || s.mechanic === 'launch_override') return;
    /* The authenticated server application receipt is final evidence that the
       discount is already on the order. It stops navigation before any local
       marker is read or written; the marker controls one-shot navigation but
       can never override provider-owned application evidence. */
    if (orderApplied(s)) return;
    if (!codeLive(s)) return;
    var key = APPLIED_PREFIX + (s.campaign_version_id || s.campaign_id || s.code);
    if (markerGet(key)) return;
    /* no durable marker means no guarantee of one-shot, and an unguarded
       redirect loop is worse than an unapplied discount. */
    if (!markerSet(key)) return;
    var url = '/discount/' + encodeURIComponent(s.code);
    var path = relPath(s.discount_redirect_path);
    if (path) url += '?redirect=' + encodeURIComponent(path);
    try { location.replace(url); } catch (e) { location.href = url; }
  }

  /* ---------------- canonical publication + Gift action ---------------- */
  var STATE = NOT_ENTITLED;
  var settled = false;
  var previousBarId = null;
  var giftInFlight = null;
  var weekendInFlight = null;
  var weekendAction = { state: 'idle', pending: false, code: null };
  var stateRevision = 0;
  var publishedSessionRef = null;
  var GIFT_REQUEST_KEY = 'exon:drops:gift-request';
  var giftAction = { state: 'idle', pending: false, code: null, detail: null, request_id: null };

  var dropsApi = window.EXON.drops || {};
  dropsApi.applicationMode = APPLY_MODE;
  dropsApi.get = function () { return copy(STATE); };
  dropsApi.getGiftAction = function () { return copy(giftAction); };
  dropsApi.reserveGift = reserveGift;
  dropsApi.claimWeekendOffer = claimWeekendOffer;
  dropsApi.selectWeekendOffer = selectWeekendOffer;
  dropsApi.getWeekendAction = function () { return copy(weekendAction); };
  dropsApi.attachLaunchGift = attachLaunchGift;
  dropsApi.getLaunchAction = function () {
    var s = STATE, u = s.usage, cart = window.EXON.cart;
    if (s.entitled && s.mechanic === 'launch_override' && u && cart && typeof cart.giftLines === 'function') {
      var lines = cart.giftLines({ launchRewardIssueId: u.reward_issue_id,
        campaignVersionId: s.campaign_version_id, variantGid: u.gift_variant_gid,
        variantId: cartVariantId(u.gift_variant_gid) }, cart.state);
      if (lines.length === 1 && verifiedGiftLine(lines[0])) return { state: 'done', pending: false, code: null };
    }
    return launchAction.state === 'done' ? { state: 'idle', pending: false, code: null } : copy(launchAction);
  };
  /* THE CHOICE SURFACES' ONLY WRITE PATH. Both islands hold no endpoint and
     no session ref; these five plus the two cart-side helpers are what they
     may call. */
  dropsApi.getChoice = function () { return STATE.entitled && STATE.choice ? JSON.parse(JSON.stringify(STATE.choice)) : null; };
  dropsApi.getChoiceAction = function () { return copy(choiceAction); };
  dropsApi.requestAnchorOffer = function () { return requestAnchorOffer(); };
  dropsApi.pickAnchorOption = function (optionId) { return pickAnchorOption(optionId); };
  dropsApi.dealMysteryHand = function () { return dealMysteryHand(); };
  dropsApi.pickMysteryCard = function (slot) { return pickMysteryCard(slot); };
  dropsApi.reportChoiceHandoff = function (grantId, report) { return reportChoiceHandoff(grantId, report); };
  dropsApi.attachChoiceGrant = function (grantId) { return attachChoiceGrant(grantId); };
  dropsApi.reportedCart = function () { return reportedCart(); };
  dropsApi.getChoiceProgress = function () { return choiceProgress(); };
  dropsApi.getGiftProgress = function () { return giftProgress(); };
  /* THE ORDINARY-OFFER DROP CONTEXT. This file alone reads the session
     store; upsell.js asks here and never touches storage itself. The opaque
     ref is handed out only while the published state is entitled AND was
     published for this very ref (another tab may have replaced it). No code,
     no campaign fact and no customer identifier ever rides this. */
  dropsApi.offerContext = function () {
    if (STATE.entitled !== true) return null;
    var rec = Store.read();
    if (!rec || !rec.ref || rec.ref !== publishedSessionRef) return null;
    return { drops_session_ref: rec.ref };
  };
  window.EXON.drops = dropsApi;

  function publishState(state) {
    stateRevision++;
    var publishedSession = Store.read();
    publishedSessionRef = publishedSession ? publishedSession.ref : null;
    if ((STATE.campaign_version_id || '') !== ((state && state.campaign_version_id) || '')) {
      launchAction = { state: 'idle', pending: false, code: null };
    }
    STATE = (state && state.entitled) ? state : NOT_ENTITLED;
    dropsApi.state = STATE;
    var item = toBarItem(STATE);
    var nextId = item ? item.id : null;
    if (previousBarId && previousBarId !== nextId) barOp('withdraw', previousBarId);
    if (item) barOp('register', item);
    previousBarId = nextId;
    try {
      document.dispatchEvent(new CustomEvent('exon:drops:state', { detail: copy(STATE) }));
    } catch (e) { /* no consumer is load-bearing */ }
  }

  function finish(state) {
    if (settled) return;
    settled = true;
    /* register BEFORE the flag drops, so the drop is painted by the time the
       strip becomes visible rather than swapping in front of the customer */
    publishState(state);
    try { document.documentElement.removeAttribute(PENDING_ATTR); } catch (e) {}
    if (STATE.entitled) maybeRedirect(STATE);
    installStateSync();
  }

  function emitWeekendAction(next, code) {
    weekendAction = { state: next, pending: /^(claiming|selecting|reconciling)$/.test(next), code: code || null };
    document.dispatchEvent(new CustomEvent('exon:drops:weekend-action', { detail: copy(weekendAction) }));
    return copy(weekendAction);
  }

  function claimWeekendOffer(key) {
    if (weekendInFlight) return weekendInFlight;
    var menu = STATE.entitled && STATE.weekend_event;
    var offer = menu && menu.offers.filter(function (o) { return o.offer_key === key; })[0];
    if (!offer || offer.held || !menu.acquisition_open || !offer.availability.claimable ||
        offer.availability.remaining < 1 || toMs(offer.ends_at) <= Date.now()) {
      return Promise.resolve(emitWeekendAction('refused', 'offer_unavailable'));
    }
    if (giftInFlight || choiceInFlight || launchInFlight) return Promise.resolve(emitWeekendAction('refused', 'action_in_progress'));
    var rec = Store.read();
    if (!rec) return Promise.resolve(emitWeekendAction('refused', 'session_unavailable'));
    emitWeekendAction('claiming');
    function reconcile() {
      emitWeekendAction('reconciling');
      return post('/state', {}, rec.ref).then(function (body) {
        var checked = ingestState(body);
        publishState(checked);
        return checked.entitled && checked.weekend_event &&
            checked.weekend_event.selected_offer_key === key
          ? emitWeekendAction('done') : emitWeekendAction('refused', 'outcome_unknown');
      }, function () { return emitWeekendAction('refused', 'outcome_unknown'); });
    }
    weekendInFlight = post('/weekend/claim', { offer_key: key }, rec.ref).then(function (body) {
      var next = ingestState(body);
      if (!next.entitled || !next.weekend_event || next.weekend_event.selected_offer_key !== key) return reconcile();
      publishState(next);
      return emitWeekendAction('done');
    }, function (err) {
      return err.kind === 'network' || err.outcomeUnknown ? reconcile() : emitWeekendAction('refused', err.code || 'claim_failed');
    }).finally(function () { weekendInFlight = null; });
    return weekendInFlight;
  }

  function selectWeekendOffer(key) {
    if (weekendInFlight) return weekendInFlight;
    var menu = STATE.entitled && STATE.weekend_event;
    var offer = menu && menu.offers.filter(function (o) { return o.offer_key === key; })[0];
    if (!offer || !offer.held || !/^(pending|ready)$/.test(offer.held.state) ||
        toMs(offer.ends_at) <= Date.now()) return Promise.resolve(emitWeekendAction('refused', 'offer_unavailable'));
    if (offer.selected) return Promise.resolve(emitWeekendAction('done'));
    if (giftInFlight || choiceInFlight || launchInFlight) return Promise.resolve(emitWeekendAction('refused', 'action_in_progress'));
    var rec = Store.read();
    if (!rec) return Promise.resolve(emitWeekendAction('refused', 'session_unavailable'));
    emitWeekendAction('selecting');
    function reconcile() {
      emitWeekendAction('reconciling');
      return post('/state', {}, rec.ref).then(function (body) {
        var checked = ingestState(body);
        publishState(checked);
        return checked.entitled && checked.weekend_event &&
            checked.weekend_event.selected_offer_key === key
          ? emitWeekendAction('done') : emitWeekendAction('refused', 'outcome_unknown');
      }, function () { return emitWeekendAction('refused', 'outcome_unknown'); });
    }
    weekendInFlight = post('/weekend/select', { offer_key: key }, rec.ref).then(function (body) {
      var next = ingestState(body);
      if (!next.entitled || !next.weekend_event || next.weekend_event.selected_offer_key !== key) return reconcile();
      publishState(next);
      return emitWeekendAction('done');
    }, function (err) {
      return err.kind === 'network' || err.outcomeUnknown ? reconcile() : emitWeekendAction('refused', err.code || 'selection_failed');
    }).finally(function () { weekendInFlight = null; });
    return weekendInFlight;
  }

  function emitGiftAction(next, code, detail, requestId) {
    giftAction = {
      state: next,
      pending: /^(reserving|reconciling|attaching)$/.test(next),
      code: code || null,
      detail: detail || null,
      request_id: requestId || null
    };
    try {
      document.dispatchEvent(new CustomEvent('exon:drops:gift-action', { detail: copy(giftAction) }));
    } catch (e) { /* presentation listener is optional */ }
    return copy(giftAction);
  }

  function uuid() {
    try {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
    } catch (e) { /* fallback below */ }
    var bytes = [];
    try {
      var a = new Uint8Array(16);
      window.crypto.getRandomValues(a);
      bytes = Array.prototype.slice.call(a);
    } catch (e) {
      for (var i = 0; i < 16; i++) bytes.push(Math.floor(Math.random() * 256));
    }
    bytes[6] = (bytes[6] & 15) | 64;
    bytes[8] = (bytes[8] & 63) | 128;
    var h = '';
    for (var j = 0; j < bytes.length; j++) h += (bytes[j] + 256).toString(16).slice(1);
    return h.slice(0, 8) + '-' + h.slice(8, 12) + '-' + h.slice(12, 16) + '-' + h.slice(16, 20) + '-' + h.slice(20);
  }

  function requestStoreRecord() {
    var raw = null;
    try { raw = window.sessionStorage.getItem(GIFT_REQUEST_KEY); } catch (e) { return null; }
    try {
      var rec = JSON.parse(raw);
      if (rec && typeof rec.version === 'string' && typeof rec.gid === 'string' &&
          /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(rec.id || '')) return rec;
    } catch (e) { /* ignore corrupt local progress */ }
    return null;
  }
  function requestStoreRead(versionId, gid) {
    var rec = requestStoreRecord();
    return rec && rec.version === versionId && rec.gid === gid ? rec.id : null;
  }
  function requestStoreWrite(versionId, gid, id) {
    try { window.sessionStorage.setItem(GIFT_REQUEST_KEY, JSON.stringify({ version: versionId, gid: gid, id: id })); } catch (e) {}
  }
  function requestStoreClear() {
    try { window.sessionStorage.removeItem(GIFT_REQUEST_KEY); } catch (e) {}
  }

  function giftClosed(gift) {
    if (!gift || gift.closed) return true;
    var cutoff = toMs(gift.cutoff_at);
    return cutoff !== null && cutoff <= Date.now();
  }
  function findGiftOption(gift, gid) {
    if (!gift || !Array.isArray(gift.options)) return null;
    for (var i = 0; i < gift.options.length; i++) if (gift.options[i].variant_gid === gid) return gift.options[i];
    return null;
  }
  function cartVariantId(gid) {
    var m = /\/(\d+)$/.exec(String(gid || ''));
    return m ? Number(m[1]) : null;
  }
  function giftIdentity(held) {
    return {
      entitlementId: held.entitlement_id,
      campaignVersionId: STATE.campaign_version_id,
      variantGid: held.variant_gid,
      variantId: cartVariantId(held.variant_gid)
    };
  }
  function quantity(line) { return Number(line && line.quantity); }
  function allocations(line) {
    var a = line && (line.line_level_discount_allocations || line.discount_allocations);
    return Array.isArray(a) ? a : [];
  }
  function positiveAllocation(a) {
    var v = a && (a.amount != null ? a.amount : a.amount_agorot);
    var n = Number(v);
    return isFinite(n) && n > 0;
  }
  function freeLine(line) {
    var total = line && (line.final_line_price != null ? line.final_line_price : line.final_price);
    return typeof total === 'number' && total === 0;
  }
  function verifiedGiftLine(line) {
    if (!line || quantity(line) !== 1 || !freeLine(line)) return false;
    return allocations(line).some(function (allocation) {
      return positiveAllocation(allocation) && allocation.discount_application &&
        allocation.discount_application.type === 'discount_code';
    });
  }

  // Discounts can change Shopify line keys. Compare the customer's contents
  // and financial result without treating a new key as a new customer line.
  function customerLines(cart, identity, state) {
    var owned = cart.giftLines(identity, state);
    function stable(value) {
      if (Array.isArray(value)) return value.map(stable);
      if (!value || typeof value !== 'object') return value;
      var out = {};
      Object.keys(value).sort().forEach(function (key) { out[key] = stable(value[key]); });
      return out;
    }
    return state.items.filter(function (line) { return owned.indexOf(line) < 0; }).map(function (line) {
      return JSON.stringify(stable({
        variant: String(line.variant_id || line.id || ''),
        quantity: line.quantity,
        properties: line.properties || {},
        selling_plan: line.selling_plan_allocation || null,
        original_line_price: line.original_line_price,
        final_line_price: line.final_line_price
      }));
    }).sort().join('\n');
  }

  function cartReady(cart) {
    return Promise.resolve(cart.ready).then(function () {
      return cart.refresh();
    }).then(function (state) {
      if (!state || !Array.isArray(state.items)) throw { code: 'cart_state_unknown' };
      return state;
    });
  }

  function compensateGift(cart, identity, attemptAdded, attempt, customerBefore) {
    var cleanup = attempt.started && typeof cart.clearAttemptedGiftDiscount === 'function'
      ? cart.clearAttemptedGiftDiscount(attempt, identity).catch(function () { return false; })
      : Promise.resolve(false);
    return cleanup.then(function () {
      if (!attemptAdded) return;
      return cart.refresh().then(function (state) {
        var found = cart.giftLines(identity, state);
        if (found.length !== 1 || quantity(found[0]) !== 1) return;
        return cart.change(found[0].key, 0);
      });
    }).then(function () {
      if (customerBefore === null) return true;
      return cart.refresh().then(function (state) {
        return customerLines(cart, identity, state) === customerBefore &&
          (!attemptAdded || cart.giftLines(identity, state).length === 0);
      });
    }).catch(function () { return false; });
  }

  function attachGift(held, requestId) {
    var cart = window.EXON.cart;
    var gift = STATE.gift;
    if (!cart || typeof cart.add !== 'function' || typeof cart.refresh !== 'function' ||
        typeof cart.change !== 'function' || typeof cart.giftLines !== 'function' ||
        typeof cart.applyDiscount !== 'function' || typeof cart.hasNonAutomaticDiscount !== 'function') {
      return Promise.resolve(emitGiftAction('refused', 'cart_unavailable', null, requestId));
    }
    if (!held || held.state !== 'reserved' || !held.code || !gift || giftClosed(gift) ||
        !gift.actions.cart_attach_allowed) {
      return Promise.resolve(emitGiftAction('refused', giftClosed(gift) ? 'gift_closed' : 'cart_attach_not_allowed', null, requestId));
    }
    var identity = giftIdentity(held);
    if (!identity.variantId) return Promise.resolve(emitGiftAction('refused', 'gift_variant_invalid', null, requestId));
    emitGiftAction('attaching', null, null, requestId);
    return attachOwnedBenefit(cart, identity, {
      _exon_gift_entitlement_id: held.entitlement_id,
      _exon_gift_campaign_version_id: STATE.campaign_version_id,
      _exon_gift_variant_gid: held.variant_gid
    }, held.code).then(function (result) {
      if (result.ok) {
        requestStoreClear();
        return emitGiftAction('done', null, null, requestId);
      }
      return emitGiftAction('refused', result.code,
        result.code === 'outcome_unknown' ? 'outcome_unknown' : null, requestId);
    });
  }

  /* The accepted Gift cart operation also attaches a committed choice reward.
     Ownership comes from its original immutable entitlement or grant, never
     from a matching variant alone. Only this operation adds or compensates. */
  function attachOwnedBenefit(cart, identity, props, benefitCode) {
    var attemptAdded = false;
    var customerBefore = null;
    var discountAttempt = { started: false, observed: null };

    return cartReady(cart).then(function (state) {
      var found = cart.giftLines(identity, state);
      if (found.length > 1 || (found.length === 1 && quantity(found[0]) !== 1)) throw { code: 'cart_identity_ambiguous' };
      if (cart.hasNonAutomaticDiscount(state)) throw { code: 'existing_discount' };
      customerBefore = customerLines(cart, identity, state);
      if (found.length === 1) return { done: false };
      function adoptedAdded(state) {
        var now = cart.giftLines(identity, state);
        if (now.length !== 1 || quantity(now[0]) !== 1) return null;
        attemptAdded = true;
        // A retained native code can make the newly added gift free already.
        // Adopt only this exact owned result with customer finances unchanged.
        return { done: verifiedGiftLine(now[0]) && customerLines(cart, identity, state) === customerBefore };
      }
      return cart.add({ id: identity.variantId, quantity: 1, properties: props }, { silent: true }).then(function (added) {
        var adopted = adoptedAdded(added);
        if (adopted) return adopted;
        return cart.refresh().then(function (fresh) {
          adopted = adoptedAdded(fresh);
          if (adopted) return adopted;
          var matches = cart.giftLines(identity, fresh);
          throw { code: matches.length ? 'cart_identity_ambiguous' : 'cart_add_failed' };
        });
      }, function () {
        return cart.refresh().then(function (fresh) {
          var adopted = adoptedAdded(fresh);
          if (adopted) return adopted;
          var matches = cart.giftLines(identity, fresh);
          throw { code: matches.length ? 'cart_identity_ambiguous' : 'cart_add_failed' };
        });
      });
    }).then(function (step) {
      if (step.done) return step;
      return cart.applyDiscount(benefitCode, discountAttempt).then(function (updated) {
        var found = cart.giftLines(identity, updated);
        if (found.length !== 1 || !verifiedGiftLine(found[0]) || customerLines(cart, identity, updated) !== customerBefore) throw { code: 'outcome_unknown' };
        return { done: true };
      }, function (err) {
        if (!discountAttempt.started) throw err;
        return cart.refresh().then(function (fresh) {
          var found = cart.giftLines(identity, fresh);
          if (found.length === 1 && verifiedGiftLine(found[0]) &&
              customerLines(cart, identity, fresh) === customerBefore) return { done: true };
          throw { code: (err && err.code) || 'outcome_unknown' };
        }, function () { throw { code: 'outcome_unknown' }; });
      });
    }).then(function () {
      return { ok: true };
    }).catch(function (err) {
      var code = (err && err.code) || 'outcome_unknown';
      return compensateGift(cart, identity, attemptAdded, discountAttempt, customerBefore).then(function (restored) {
        if (!restored) code = 'cart_restore_unknown';
        return { ok: false, code: code };
      });
    });
  }

  var launchInFlight = null;
  var launchAction = { state: 'idle', pending: false, code: null };
  function emitLaunchAction(state, code) {
    launchAction = { state: state, pending: state === 'attaching', code: code || null };
    try { document.dispatchEvent(new CustomEvent('exon:drops:launch-action', { detail: copy(launchAction) })); } catch (e) {}
    return copy(launchAction);
  }
  function attachLaunchGift() {
    if (launchInFlight) return launchInFlight;
    var s = STATE;
    var u = s.usage;
    if (!s.entitled || s.mechanic !== 'launch_override' || !u || u.policy !== 'launch_bxgy_v1' ||
        !s.campaign_version_id || !codeLive(s) || orderApplied(s)) {
      return Promise.resolve(emitLaunchAction('refused', 'launch_unavailable'));
    }
    var cart = window.EXON.cart;
    if (!cart || ['add', 'refresh', 'change', 'giftLines', 'applyDiscount', 'hasNonAutomaticDiscount'].some(function (key) {
      return typeof cart[key] !== 'function';
    })) return Promise.resolve(emitLaunchAction('refused', 'cart_unavailable'));
    var identity = {
      launchRewardIssueId: u.reward_issue_id, campaignVersionId: s.campaign_version_id,
      variantGid: u.gift_variant_gid, variantId: cartVariantId(u.gift_variant_gid)
    };
    if (!identity.variantId) return Promise.resolve(emitLaunchAction('refused', 'gift_variant_invalid'));
    emitLaunchAction('attaching', null);
    launchInFlight = attachOwnedBenefit(cart, identity, {
      _exon_launch_reward_issue_id: u.reward_issue_id,
      _exon_launch_campaign_version_id: s.campaign_version_id,
      _exon_launch_variant_gid: u.gift_variant_gid
    }, s.code).then(function (result) {
      return emitLaunchAction(result.ok ? 'done' : 'refused', result.code);
    }).finally(function () { launchInFlight = null; });
    return launchInFlight;
  }

  function reserveGift(optionVariantGid) {
    if (weekendInFlight) return Promise.resolve(emitGiftAction('refused', 'action_in_progress', null, null));
    if (giftInFlight) return giftInFlight;
    var gift = STATE.gift;
    var gid = str(optionVariantGid);
    var selected = findGiftOption(gift, gid);
    if (!STATE.entitled || !gift || !gid || !selected) return Promise.resolve(emitGiftAction('refused', 'gift_option_unknown', null, null));
    if (giftClosed(gift)) return Promise.resolve(emitGiftAction('refused', 'gift_closed', null, null));

    var versionId = STATE.campaign_version_id || '';
    var held = gift.held;
    var unresolved = requestStoreRecord();
    if (!held && unresolved && unresolved.version === versionId && unresolved.gid !== gid) {
      return Promise.resolve(emitGiftAction('refused', 'outcome_unknown', 'outcome_unknown', unresolved.id));
    }
    if (!selected.available && !held) return Promise.resolve(emitGiftAction('refused', 'gift_option_exhausted', null, null));
    var requestId = requestStoreRead(versionId, gid) || uuid();
    if (held) {
      requestStoreClear();
      giftInFlight = attachGift(held, requestId).finally(function () { giftInFlight = null; });
      return giftInFlight;
    }
    if (!gift.actions.reserve_allowed) return Promise.resolve(emitGiftAction('refused', 'gift_reserve_not_allowed', null, requestId));
    var rec = Store.read();
    if (!rec) return Promise.resolve(emitGiftAction('refused', 'session_unavailable', null, requestId));
    requestStoreWrite(versionId, gid, requestId);
    emitGiftAction('reserving', null, null, requestId);
    var cartTotal = window.EXON.cart && window.EXON.cart.state && window.EXON.cart.state.total_price;
    var request = {
      option_variant_gid: gid,
      client_cart_total_agorot: (typeof cartTotal === 'number' && isFinite(cartTotal)) ? Math.floor(cartTotal) : null,
      client_cart_digest: null,
      request_id: requestId
    };
    function reconcileReserve() {
      emitGiftAction('reconciling', null, null, requestId);
      return post('/state', {}, rec.ref).then(function (body) {
        var next = ingestState(body);
        publishState(next);
        if (next.entitled && next.gift && next.gift.held) {
          requestStoreClear();
          return attachGift(next.gift.held, requestId);
        }
        return emitGiftAction('refused', 'outcome_unknown', 'outcome_unknown', requestId);
      }, function () {
        return emitGiftAction('refused', 'outcome_unknown', 'outcome_unknown', requestId);
      });
    }
    giftInFlight = post('/gift/reserve', request, rec.ref).then(function (body) {
      var next = ingestState(body);
      if (!next.entitled || !next.gift || !next.gift.held) return reconcileReserve();
      publishState(next);
      requestStoreClear();
      return attachGift(next.gift.held, requestId);
    }, function (err) {
      if (err.kind === 'network' || err.outcomeUnknown === true) return reconcileReserve();
      requestStoreClear();
      return emitGiftAction('refused', err.code || 'gift_reserve_failed', null, requestId);
    }).finally(function () { giftInFlight = null; });
    return giftInFlight;
  }

  /* ============================================================
     THE FIVE E3 CHOICE ROUTES.

     drops.js stays the SOLE caller of the drops API, so the Anchor case and
     the Mystery hand hold no endpoint, no session ref and no fetch: they call
     the methods below and read what comes back. The request bodies match
     handler.ts's closed key sets exactly (CHOICE_ROUTE_KEYS) — an unknown key
     is `malformed_request`, so there is nothing to be relaxed about here.

     WHAT THE BROWSER IS AND IS NOT ALLOWED TO SAY. `cart` is the browser's
     ACCOUNT OF ITS OWN CART and the only input any of these routes takes
     toward money. The qualifying subtotal, the cart digest and the
     measurement holdout are all derived from it SERVER SIDE (migration
     20261002006741), which is why there is no `qualifying_subtotal_agorot`,
     no `cart_digest` and no `holdout` key to send and none is invented.

     ONE OPERATION AT A TIME, AND NEVER A SECOND ATTEMPT. `choiceInFlight`
     locks ONE in-flight write, not the mechanic: whether another attempt is
     available afterwards is derived from real state, never from a client-side
     "already played" flag. An unknown outcome takes exactly one `/state`
     reconciliation; it never re-posts a pick and it never issues a benefit
     locally.
     ============================================================ */
  var choiceInFlight = null;
  var choiceAction = { state: 'idle', pending: false, code: null, detail: null, request_id: null, result: null };
  var CHOICE_PENDING_RE = /^(offering|picking|dealing|reconciling|attaching|reporting)$/;

  function emitChoiceAction(next, code, detail, requestId, result) {
    choiceAction = {
      state: next,
      pending: CHOICE_PENDING_RE.test(next),
      code: code || null,
      detail: detail || null,
      request_id: requestId || null,
      result: result || null
    };
    try {
      document.dispatchEvent(new CustomEvent('exon:drops:choice-action', { detail: copy(choiceAction) }));
    } catch (e) { /* presentation listener is optional */ }
    return copy(choiceAction);
  }

  /* ------------------------------------------------------------
     THE REPORTED CART — from window.EXON.cart, the authoritative Shopify cart
     response, and from nowhere else. ONE WIRE LINE PER CART LINE, in cart
     order, exactly five keys:
       {line_key, variant_gid, quantity, line_subtotal_agorot, properties}

     NOTHING IS COALESCED. Shopify's cart legitimately holds the same variant
     on two lines whenever their line-item properties differ (gift
     attribution, upsell attribution, upgrade undo stamps), and Drop's server
     keys each reported line by Shopify's own line key, so two lines of one
     variant are two facts and travel as two lines. A line key is never
     synthesized: a line without its own key, or a key seen twice, cannot be
     stated truthfully and the whole report refuses.

     line_subtotal_agorot is Shopify's final_line_price as given (already in
     agorot), or null when it is not a safe non-negative integer. A null is
     SENT — it means unknown — and never refuses the report; it is not
     backfilled from line_price either. properties carries only the
     _exon_gift_* stamps, copied into a fresh object; the raw cart line is
     never mutated.

     ANYTHING ELSE THAT CANNOT BE STATED TRUTHFULLY REFUSES LOCALLY rather
     than being clamped into range: a quantity outside 1..1000 on its own line
     is not reported as the ceiling, and a cart of more than 100 lines is
     refused whole (cart_too_large, the code the server's own size cap uses)
     before anything is built — never truncated.
     ------------------------------------------------------------ */
  var CART_VARIANT_GID_RE = /^gid:\/\/shopify\/ProductVariant\/[0-9]+$/;
  var MAX_REPORTED_LINES = 100;
  var MAX_REPORTED_QUANTITY = 1000;
  var MAX_LINE_KEY_LENGTH = 128;
  var GIFT_PROPERTY_PREFIX = '_exon_gift_';

  function lineVariantGid(line) {
    var id = line && (line.variant_id != null ? line.variant_id : line.id);
    if (typeof id === 'number' && isFinite(id) && Math.floor(id) === id && id > 0) {
      return 'gid://shopify/ProductVariant/' + id;
    }
    if (typeof id === 'string' && /^[0-9]+$/.test(id)) {
      return 'gid://shopify/ProductVariant/' + id;
    }
    return null;
  }
  function lineTotalAgorot(line) {
    var v = line && (line.final_line_price != null ? line.final_line_price : line.line_price);
    return (typeof v === 'number' && isFinite(v) && v >= 0 && Math.floor(v) === v) ? v : null;
  }
  /* the reported line's subtotal: final_line_price only, strictly. Unlike
     lineTotalAgorot (Gift progress) there is no line_price fallback — a
     price Shopify did not finalize is unknown, and unknown is null. */
  function lineSubtotalAgorot(line) {
    var v = line && line.final_line_price;
    return (typeof v === 'number' && Number.isSafeInteger(v) && v >= 0) ? v : null;
  }
  /* only the _exon_gift_* stamps with a non-empty string value; the legacy
     _exon_gift key (no trailing underscore) is not one of them. */
  function lineGiftProperties(line) {
    var out = {};
    var props = plainObject(line && line.properties);
    if (!props) return out;
    var keys = Object.keys(props);
    for (var i = 0; i < keys.length; i++) {
      var v = props[keys[i]];
      if (keys[i].indexOf(GIFT_PROPERTY_PREFIX) === 0 && typeof v === 'string' && v.length) {
        out[keys[i]] = v;
      }
    }
    return out;
  }

  /* {ok: true, cart: [...]} or {ok: false, code: '<why>'} — a refusal names
     itself so a surface can say something true instead of failing silently.
     Codes: cart_unavailable | cart_line_unreportable | cart_too_large. */
  function reportedCart() {
    var store = window.EXON.cart;
    var state = store && store.state;
    if (!state || !Array.isArray(state.items)) return { ok: false, code: 'cart_unavailable' };
    if (state.items.length > MAX_REPORTED_LINES) return { ok: false, code: 'cart_too_large' };
    var out = [];
    var seenKey = Object.create(null);
    for (var i = 0; i < state.items.length; i++) {
      var line = state.items[i];
      var key = line && line.key;
      var gid = lineVariantGid(line);
      var qty = line && line.quantity;
      if (typeof key !== 'string' || !key.length || key.length > MAX_LINE_KEY_LENGTH || seenKey[key]) {
        return { ok: false, code: 'cart_line_unreportable' };
      }
      if (!gid || !CART_VARIANT_GID_RE.test(gid)) return { ok: false, code: 'cart_line_unreportable' };
      if (typeof qty !== 'number' || !Number.isSafeInteger(qty) || qty < 1 || qty > MAX_REPORTED_QUANTITY) {
        return { ok: false, code: 'cart_line_unreportable' };
      }
      seenKey[key] = true;
      out.push({
        line_key: key,
        variant_gid: gid,
        quantity: qty,
        line_subtotal_agorot: lineSubtotalAgorot(line),
        properties: lineGiftProperties(line)
      });
    }
    return { ok: true, cart: out };
  }

  // Display arithmetic over the same reported cart and authored variant set
  // used by choice_cart_qualifying_subtotal. This never authorizes a reward;
  // the server rechecks the action and Shopify enforces checkout conditions.
  // The authored variant list has its own bound, unrelated to the cart's.
  var MAX_QUALIFYING_VARIANTS = 250;
  function choiceProgress() {
    if (!STATE.entitled || !/^(anchor_reward|mystery_cards)$/.test(STATE.mechanic || '')) return null;
    var pub = STATE.choice && STATE.choice.public;
    if (!pub || pub.mechanic !== STATE.mechanic) return null;
    /* the Stage 3 goal block, when the server sent a valid one */
    var goal = plainObject(pub.goal);
    var minimum = nonNegInt(pub.min_qualifying_subtotal_agorot);
    if (minimum === null && goal) minimum = nonNegInt(goal.target_agorot);
    if (minimum === null) return null;
    /* minimum_quantity null = unknown (older server): amount-only, as before.
       eligible_quantity is measured over the SAME lines as the subtotal. */
    var result = {
      minimum_subtotal_agorot: minimum,
      eligible_subtotal_agorot: null,
      minimum_quantity: goal ? nonNegInt(goal.minimum_quantity) : null,
      eligible_quantity: null
    };
    var gids = (goal && Array.isArray(goal.qualifying_variant_gids))
      ? goal.qualifying_variant_gids : pub.qualifying_variant_gids;
    if (!Array.isArray(gids) || !gids.length || gids.length > MAX_QUALIFYING_VARIANTS) return result;
    var buy = Object.create(null);
    for (var i = 0; i < gids.length; i++) {
      if (typeof gids[i] !== 'string' || !CART_VARIANT_GID_RE.test(gids[i]) || buy[gids[i]]) return result;
      buy[gids[i]] = true;
    }
    var report = reportedCart();
    if (!report.ok) return result;
    /* one reported line per cart line, so a variant split across lines counts
       on every line it occupies. One counted line with an unknown subtotal
       makes the sum unknown (null, never 0), and the quantity is published
       only beside a known subtotal. */
    var total = 0;
    var quantity = 0;
    var unknown = false;
    report.cart.forEach(function (line) {
      if (!buy[line.variant_gid]) return;
      if (line.line_subtotal_agorot === null) { unknown = true; return; }
      total += line.line_subtotal_agorot;
      quantity += line.quantity;
    });
    if (!unknown && isFinite(total) && total <= Number.MAX_SAFE_INTEGER) {
      result.eligible_subtotal_agorot = total;
      result.eligible_quantity = quantity;
    }
    return result;
  }

  /* STAGE 4 GIFT PROGRESS — display arithmetic over the selected option's
     collection scope. Membership is NOT derived here: it comes only from the
     joined Section Rendering fragment drops-membership.js publishes, which
     is already checked against this exact cart (token, keys, variants,
     products, quantities). Anything not joined is unknown (null), never a
     zero. The reserved gift line(s) this entitlement owns are excluded: the
     gift never counts toward its own condition. Shopify still decides the
     checkout; this never authorizes anything. */
  function giftProgress() {
    if (!STATE.entitled) return null;
    var g = STATE.gift;
    if (!g || !Array.isArray(g.options)) return null;
    var sel = whole(g.selected_option);
    var option = sel === null ? null : g.options[sel];
    var goal = option && option.goal;
    var scope = goal && goal.scope;
    if (!scope || !scope.collection_gid) return null;
    var api = window.EXON.drops;
    var membership = null;
    try {
      membership = api && typeof api.getGiftMembership === 'function' ? api.getGiftMembership() : null;
    } catch (e) { membership = null; }
    if (!membership || membership.collection_gid !== scope.collection_gid ||
        !plainObject(membership.members)) return null;
    var cart = window.EXON.cart;
    var state = cart && cart.state;
    if (!state || !Array.isArray(state.items)) return null;
    var owned = [];
    if (g.held) {
      if (typeof cart.giftLines !== 'function') return null;
      owned = cart.giftLines(giftIdentity(g.held), state);
    }
    var total = 0;
    for (var i = 0; i < state.items.length; i++) {
      var line = state.items[i];
      var key = line && line.key;
      if (typeof key !== 'string' || !Object.prototype.hasOwnProperty.call(membership.members, key)) return null;
      var member = membership.members[key];
      if (typeof member !== 'boolean') return null;
      if (!member || owned.indexOf(line) >= 0) continue;
      var lineTotal = lineTotalAgorot(line);
      if (lineTotal === null) return null;
      total += lineTotal;
    }
    if (!isFinite(total) || total > Number.MAX_SAFE_INTEGER) return null;
    return {
      advertised_agorot: goal.advertised_agorot,
      enforced_agorot: goal.enforced_agorot,
      eligible_subtotal_agorot: total
    };
  }

  /* the authoritative cart, refreshed through its own owner when this page has
     not read one yet. exon-store.js is the cart network owner; this never
     fetches /cart.js itself. */
  function withReportedCart() {
    var first = reportedCart();
    if (first.ok || first.code !== 'cart_unavailable') return Promise.resolve(first);
    var store = window.EXON.cart;
    if (!store || typeof store.refresh !== 'function') return Promise.resolve(first);
    return Promise.resolve(store.refresh()).then(function () {
      return reportedCart();
    }, function () {
      return { ok: false, code: 'cart_unavailable' };
    });
  }

  /* the choice block as the surfaces read it, and the current session ref */
  function choiceState() { return STATE.entitled ? STATE.choice : null; }

  function choiceResult(body) {
    var r = body && body.result;
    if (!r || typeof r !== 'object' || Array.isArray(r)) return null;
    var out = copy(r);
    /* THE HOLDOUT NEVER REACHES A SURFACE. commit_anchor_offer returns it in
       the route result even though the state projection omits it; a member who
       learns they are in the measurement holdout behaves differently. */
    delete out.holdout;
    return out;
  }

  /* ONE reconciliation, and only one. The write's outcome is unknown, so the
     next fact comes from `/state` — the contract's sole reconciliation truth —
     and the surface is told the outcome was unknown either way. */
  function reconcileChoice(ref, requestId) {
    emitChoiceAction('reconciling', null, null, requestId, null);
    return post('/state', {}, ref).then(function (body) {
      publishState(ingestState(body));
      return emitChoiceAction('refused', 'outcome_unknown', 'outcome_unknown', requestId, null);
    }, function () {
      return emitChoiceAction('refused', 'outcome_unknown', 'outcome_unknown', requestId, null);
    });
  }

  /* every economic choice write goes through here: lock, reported cart,
     session, post, ingest, and one reconciliation on an unknown outcome. */
  function choiceWrite(opts) {
    if (choiceInFlight) return choiceInFlight;
    if (!STATE.entitled || !STATE.choice) {
      return Promise.resolve(emitChoiceAction('refused', 'choice_unavailable', null, null, null));
    }
    var rec = Store.read();
    if (!rec) return Promise.resolve(emitChoiceAction('refused', 'session_unavailable', null, null, null));
    var requestId = uuid();
    emitChoiceAction(opts.busy, null, null, requestId, null);
    choiceInFlight = withReportedCart().then(function (report) {
      if (!report.ok) return emitChoiceAction('refused', report.code, null, requestId, null);
      var body = opts.body(report.cart, requestId);
      return post(opts.path, body, rec.ref).then(function (envelope) {
        publishState(ingestState(envelope));
        var result = choiceResult(envelope);
        if (!result || typeof result.outcome !== 'string') {
          /* a 200 without a readable outcome is an unknown outcome */
          return reconcileChoice(rec.ref, requestId);
        }
        return emitChoiceAction(opts.settle(result), null, null, requestId, result);
      }, function (err) {
        if (err.kind === 'network' || err.outcomeUnknown === true) {
          return reconcileChoice(rec.ref, requestId);
        }
        return emitChoiceAction('refused', err.code || 'choice_write_failed', null, requestId, null);
      });
    }).finally(function () { choiceInFlight = null; });
    return choiceInFlight;
  }

  /* `outcome` is the SERVER'S word for what happened, mapped to the one word
     this client's surfaces switch on. Nothing is inferred: an outcome this
     client does not know is `refused`, not a guess at success. */
  function settleAnchor(result) {
    if (result.outcome === 'offered' || result.outcome === 'no_offer') return 'offered';
    if (result.outcome === 'recorded') return 'recorded';
    if (result.outcome === 'already_recorded') return 'recorded';
    if (result.outcome === 'stale' || result.outcome === 'refused') return 'stale';
    return 'refused';
  }
  function settleMystery(result) {
    if (result.outcome === 'dealt') return 'dealt';
    if (result.outcome === 'win') return 'recorded';
    if (result.outcome === 'blank') return 'blank';
    if (result.outcome === 'stale' || result.outcome === 'unavailable') return 'stale';
    return 'refused';
  }

  function requestAnchorOffer() {
    return choiceWrite({
      path: '/anchor/offer',
      busy: 'offering',
      settle: settleAnchor,
      body: function (cart, requestId) { return { cart: cart, request_id: requestId }; }
    });
  }

  /* THE PICK SUBMITS `option_id`, WHICH IS THE OFFER'S OWN IDENTITY. It is
     not `variant_gid` under another name: pick_anchor_offer looks the option
     up by option_id inside the offer it froze, and a variant substituted for
     it raises drops.anchor_option_not_offered. */
  function pickAnchorOption(optionId) {
    var id = str(optionId);
    var choice = choiceState();
    var offer = choice && choice.offer;
    if (!offer) return Promise.resolve(emitChoiceAction('refused', 'choice_unavailable', null, null, null));
    var known = false;
    for (var i = 0; i < offer.options.length; i++) {
      if (offer.options[i].option_id === id) known = true;
    }
    if (!id || !known) return Promise.resolve(emitChoiceAction('refused', 'choice_option_unknown', null, null, null));
    return choiceWrite({
      path: '/anchor/pick',
      busy: 'picking',
      settle: settleAnchor,
      body: function (cart, requestId) {
        return {
          anchor_offer_id: offer.anchor_offer_id,
          option_id: id,
          cart: cart,
          request_id: requestId
        };
      }
    });
  }

  function dealMysteryHand() {
    return choiceWrite({
      path: '/mystery/deal',
      busy: 'dealing',
      settle: settleMystery,
      body: function (cart, requestId) { return { cart: cart, request_id: requestId }; }
    });
  }

  /* THE SLOT IS THE SERVER'S OWN INTEGER, submitted unchanged. The surface's
     cards are local representations of `slot_count`; slot n on screen is slot
     n on the wire, and no slot is ever associated with a prize anywhere in
     this client. `expected_hand_digest` is the browser's account of WHICH HAND
     it is looking at, which is only meaningful because the server did not
     supply it back — a same-hand replay and a changed hand are told apart by
     it. */
  function pickMysteryCard(slot) {
    var choice = choiceState();
    var attempt = choice && choice.attempt;
    if (!attempt) return Promise.resolve(emitChoiceAction('refused', 'choice_unavailable', null, null, null));
    var n = Number(slot);
    if (!isFinite(n) || Math.floor(n) !== n || n < 0 || n >= attempt.slot_count) {
      return Promise.resolve(emitChoiceAction('refused', 'choice_option_unknown', null, null, null));
    }
    return choiceWrite({
      path: '/mystery/pick',
      busy: 'picking',
      settle: settleMystery,
      body: function (cart, requestId) {
        return {
          mystery_attempt_id: attempt.mystery_attempt_id,
          slot: n,
          cart: cart,
          expected_hand_digest: attempt.hand_digest,
          request_id: requestId
        };
      }
    });
  }

  /* ------------------------------------------------------------
     THE HANDOFF — what the BROWSER managed to do with a benefit it already
     holds. It moves no money, takes no cart and is posted AFTER the actual
     cart action, never before it. `attached | add_failed | attach_failed` is
     the handler's closed set (HANDOFF_REPORTS).
     ------------------------------------------------------------ */
  var HANDOFF_REPORT_RE = /^(attached|add_failed|attach_failed)$/;
  function reportChoiceHandoff(grantId, report) {
    var id = str(grantId);
    var rec = Store.read();
    if (!id || !HANDOFF_REPORT_RE.test(String(report)) || !rec) return Promise.resolve(null);
    return post('/choice/handoff', { choice_grant_id: id, report: String(report) }, rec.ref)
      .then(function (body) {
        publishState(ingestState(body));
        return choiceResult(body);
      }, function () {
        /* a report that did not land changes nothing the member holds */
        return null;
      });
  }

  function findGrant(id) {
    var choice = choiceState();
    var grants = choice ? choice.grants : [];
    for (var i = 0; i < grants.length; i++) {
      if (grants[i].choice_grant_id === id) return grants[i];
    }
    return null;
  }

  /* ------------------------------------------------------------
     ATTACHING A COMMITTED GRANT TO THE CART.

     A COMMITTED WIN WHOSE ISSUANCE IS PENDING SHOWS PENDING. `code_available`
     is READINESS, not disclosure: only a code the server actually returned can
     be applied, and this function applies nothing else. There is no local
     issuance, no synthesised applied state and no checkout or order receipt.

     Failure compensates only what THIS attempt did, through the cart's own
     owner, and reconciles the authoritative cart before compensating. An
     unrelated line or an existing discount is never undone.
     ------------------------------------------------------------ */
  function attachChoiceGrant(grantId) {
    if (choiceInFlight) return choiceInFlight;
    var id = str(grantId);
    var grant = findGrant(id);
    var cart = window.EXON.cart;
    if (!grant) return Promise.resolve(emitChoiceAction('refused', 'choice_grant_unknown', null, null, null));
    if (!cart || typeof cart.applyDiscount !== 'function' || typeof cart.refresh !== 'function') {
      return Promise.resolve(emitChoiceAction('refused', 'cart_unavailable', null, null, null));
    }
    if (!grant.code_available) {
      return Promise.resolve(emitChoiceAction('pending', 'issuance_pending', null, null, null));
    }
    if (!grant.code) {
      /* bound, but not disclosed to THIS member — readiness without a code is
         not something the browser may work around. */
      return Promise.resolve(emitChoiceAction('pending', 'code_not_disclosed', null, null, null));
    }
    if (grant.state === 'redeemed') {
      return Promise.resolve(emitChoiceAction('refused', 'grant_redeemed', null, null, null));
    }
    if (grant.state !== 'issued') {
      return Promise.resolve(emitChoiceAction('pending', 'issuance_pending', null, null, null));
    }
    if (!grant.no_expiry && (toMs(grant.ends_at) === null || toMs(grant.ends_at) <= Date.now())) {
      return Promise.resolve(emitChoiceAction('refused', 'grant_expired', null, null, null));
    }
    if (typeof cart.add !== 'function' || typeof cart.change !== 'function' ||
        typeof cart.giftLines !== 'function' || typeof cart.hasNonAutomaticDiscount !== 'function') {
      return Promise.resolve(emitChoiceAction('refused', 'cart_unavailable', null, null, null));
    }
    var identity = { choiceGrantId: id, variantGid: grant.variant_gid,
      variantId: cartVariantId(grant.variant_gid) };
    if (!identity.variantId) return Promise.resolve(emitChoiceAction('refused', 'gift_variant_invalid', null, null, null));
    emitChoiceAction('attaching', null, null, null, null);
    choiceInFlight = attachOwnedBenefit(cart, identity, {
      _exon_choice_grant_id: id,
      _exon_choice_variant_gid: grant.variant_gid
    }, grant.code).then(function (result) {
      var report = result.ok ? 'attached' : result.code === 'cart_add_failed' ? 'add_failed' : 'attach_failed';
      return reportChoiceHandoff(id, report).then(function () {
        return result.ok ? emitChoiceAction('attached', null, null, null, null)
          : emitChoiceAction('refused', result.code, null, null, null);
      });
    }).finally(function () { choiceInFlight = null; });
    return choiceInFlight;
  }

  /* ---------------- flow ---------------- */
  function exchange(token, stored) {
    return postOnce('/session', { exd: token }, null).then(function (body) {
      var sess = normalizeSession(body && body.session);
      if (sess) Store.write(sess);
      stripExd(); /* only after a SUCCESSFUL exchange — a retryable failure
                     must leave the customer a URL they can reload */
      finish(ingestState(body));
    }, function (err) {
      /* a failed NEW token never evicts an already-stored session: the server
         judged the token, and said nothing about the ref we already hold. */
      if (stored && canFollowUp(err)) return refresh(stored);
      finish(NOT_ENTITLED);
    });
  }

  /* A follow-up is a SECOND request from a visitor who has already made one,
     at the minute when 8,000 of them arrive together. The question that
     decides it is NOT how severe the failure was — it is whether the failure
     can CORRELATE ACROSS CUSTOMERS.

     · Correlated / server-side — internal_error, any 5xx, rate_limited,
       rate_limit_unavailable, storefront_unavailable. Eight thousand people
       meet these in the same instant, so a follow-up doubles the load on a
       dependency that is already failing. Never follow up.
     · Token-local / per-customer — invalid_or_expired_credential and
       malformed_request. Both depend on the one URL that one customer arrived
       with, so they cannot pile into a herd, and falling back to that
       customer's valid stored session is the entire point: a member holding a
       live session who taps an old or mangled link is entitled, and must not
       be shown nothing because of a 400.

     Keep this an ALLOW-list. A deny-list of the three shedding codes reads as
     equivalent and is not — it lets every future error code default to
     "follow up", which is how the amplification gets back in. */
  var TOKEN_LOCAL_RE = /^(invalid_or_expired_credential|malformed_request)$/;
  function canFollowUp(err) {
    return err.kind === 'api' && TOKEN_LOCAL_RE.test(err.code || '');
  }

  function refresh(rec) {
    return postOnce('/state', {}, rec.ref).then(function (body) {
      var sess = normalizeSession(body && body.session);
      if (sess) Store.write(sess);
      finish(ingestState(body));
    }, function (err) {
      /* the ONLY condition that clears the stored ref: the server told us this
         exact credential is dead. Everything else keeps it for the next page —
         a rate limit is not evidence that a session expired. */
      if (err.kind === 'api' && err.code === 'invalid_or_expired_credential') Store.clear();
      finish(NOT_ENTITLED);
    });
  }

  /* Reuse this client for live ticket reads. Opening/changing the cart or
     returning to the page wakes it; a visible ticket also refreshes every
     30–45 seconds. One read at a time, no background polling, and no automatic
     transport retry. A newer action response always wins over an older read. */
  var syncTimer = null;
  var syncInFlight = null;
  var syncAfter = 0;
  var syncInstalled = false;

  function syncVisible() {
    return document.visibilityState !== 'hidden';
  }

  function ticketSurfaceOpen() {
    return typeof document.querySelector === 'function' && !!document.querySelector(
      '[data-drop-ticket][data-dtk-surface="cart"], [data-exon-popup]:not([hidden]), [data-exon-drawer]:not([hidden])'
    );
  }

  function scheduleStateSync() {
    if (syncTimer) clearTimeout(syncTimer);
    syncTimer = null;
    if (!Store.read()) {
      if (STATE.entitled) publishState(NOT_ENTITLED);
      return;
    }
    if (!syncVisible() || !ticketSurfaceOpen()) return;
    var wait = Math.max(30000 + Math.random() * 15000, syncAfter - Date.now());
    syncTimer = setTimeout(function () { syncTimer = null; syncState(); }, wait);
  }

  function syncState() {
    if (!syncVisible()) return scheduleStateSync();
    if (syncInFlight) return syncInFlight;
    if (weekendInFlight || giftInFlight || choiceInFlight || Date.now() < syncAfter) return scheduleStateSync();
    var rec = Store.read();
    if (!rec) {
      if (STATE.entitled) publishState(NOT_ENTITLED);
      return scheduleStateSync();
    }
    var revision = stateRevision;
    syncAfter = Date.now() + 10000;
    function currentRead() {
      var current = Store.read();
      return current && current.ref === rec.ref && stateRevision === revision && !weekendInFlight && !giftInFlight && !choiceInFlight && !launchInFlight;
    }
    syncInFlight = post('/state', {}, rec.ref).then(function (body) {
      if (!currentRead()) return;
      var sess = normalizeSession(body && body.session);
      if (sess) Store.write(sess);
      publishState(ingestState(body));
    }, function (err) {
      // Cart events cannot bypass a server cooldown or create a retry herd.
      syncAfter = Date.now() + Math.max(60000 + Math.random() * 30000,
        (err.retryAfterSeconds || 0) * 1000);
      if (!currentRead()) return;
      if (err.kind === 'api' && err.code === 'invalid_or_expired_credential') Store.clear();
      publishState(NOT_ENTITLED);
    }).finally(function () {
      syncInFlight = null;
      scheduleStateSync();
    });
    return syncInFlight;
  }

  function installStateSync() {
    if (syncInstalled || !ENABLED || !Store.read() ||
        typeof document.addEventListener !== 'function') return;
    syncInstalled = true;
    syncAfter = Date.now() + 10000;
    document.addEventListener('exon:cart', syncState);
    document.addEventListener('exon:overlay:opened', syncState);
    document.addEventListener('exon:overlay:closed', scheduleStateSync);
    document.addEventListener('visibilitychange', syncState);
    if (typeof window.addEventListener === 'function') {
      window.addEventListener('pageshow', syncState);
      window.addEventListener('focus', syncState);
      window.addEventListener('storage', function (event) {
        if (event.key !== STORAGE_KEY && event.key !== null) return;
        var current = Store.read();
        if (publishedSessionRef !== (current ? current.ref : null)) publishState(NOT_ENTITLED);
        syncState();
      });
    }
    scheduleStateSync();
  }

  /* Public discovery shares this transport and the existing bar registry,
     but never ingests a session, persists a credential or applies a code. */
  var publicIds = [];
  var publicTimer = null;
  var publicInFlight = null;
  var publicAfter = 0;
  var publicHealthy = false;
  var publicInstalled = false;
  /* the FIRST /public read is still out. The bar island holds its one-time
     collapse on this (published as dropsApi.publicPending, kept in step), so a
     campaign that answers after the session settles is painted rather than
     shut out by an already-collapsed shell. Later reads never touch it. */
  var publicFirstPending = false;
  var PUBLIC_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  /* the bot's compact invite (48-char start token), OR a plain t.me group /
     channel handle — the owner's own link for the public join card */
  var PUBLIC_JOIN = /^https:\/\/t\.me\/[A-Za-z][A-Za-z0-9_]{4,31}(?:\?start=[A-Za-z0-9_-]{48})?$/;
  /* the theme's own join link (settings.drops_join_url) wins over the engine's
     bot invite when it is a valid t.me link; the join WINDOW still comes from
     the engine, so the card appears and expires exactly as before */
  var JOIN_URL = String(CFG.joinUrl == null ? '' : CFG.joinUrl).trim();

  function publicSurface() {
    return typeof document.querySelector === 'function' &&
      !!document.querySelector('[data-exon-annc]');
  }
  function publicText(value, ceiling) {
    return typeof value === 'string' && value.trim() && value.length <= ceiling ? value.trim() : null;
  }
  function replacePublic(items) {
    var nextIds = items.map(function (item) { return item.id; });
    publicIds.forEach(function (id) { if (nextIds.indexOf(id) < 0) barOp('withdraw', id); });
    items.forEach(function (item) { barOp('register', item); });
    publicIds = nextIds;
  }
  function publicItems(body) {
    var value = plainObject(body && body.public);
    if (!value || !Array.isArray(value.campaigns)) return [];
    var now = Date.now();
    var items = [];
    var seen = Object.create(null);
    var lang = document.documentElement && document.documentElement.getAttribute('lang');
    var en = /^en(?:-|$)/i.test(lang || '');
    /* the theme's own join label and the join link, resolved ONCE: the join
       card and a members campaign shown to a visitor send to the same place
       under the same words */
    var joinCta = en ? 'Join on Telegram' : 'הצטרפות בטלגרם';
    var joinLink = (JOIN_URL && PUBLIC_JOIN.test(JOIN_URL)) ? JOIN_URL
      : (typeof value.join_url === 'string' && PUBLIC_JOIN.test(value.join_url)) ? value.join_url : null;
    value.campaigns.slice(0, 10).forEach(function (row) {
      var r = plainObject(row);
      if (!r || !PUBLIC_UUID.test(r.campaign_id || '') || !PUBLIC_UUID.test(r.campaign_version_id || '')) return;
      var headline = publicText(r.headline, 240);
      var start = toMs(r.window_start_at);
      var end = toMs(r.window_end_at);
      /* A NULL DESTINATION IS A TRUTHFUL "NO LINK" (legacy, invalid or refused
         destination): the words still render and the CTA is omitted — never a
         `/` fallback. A STRING that is not a safe store path still refuses
         the whole item, exactly as before. Only `resolved` and `degraded`
         (or an older server that sends no state) may carry a link. */
      var path = null;
      if (r.destination_path !== null) {
        path = typeof r.destination_path === 'string' ? relPath(r.destination_path) : '';
        if (!path || /\\/.test(path)) return;
      }
      var destState = r.destination_state;
      if (destState != null && destState !== 'resolved' && destState !== 'degraded') path = null;
      if (!headline || start === null || end === null || start > now || end <= now || end <= start) return;
      if (seen[r.campaign_id]) return;
      seen[r.campaign_id] = true;
      /* THE TYPED BAR (the Drop Coupon item). The server authors the words
         and sends them as one object, or `bar: null` when it has none (or
         refused them - over-length is the server's call, 240 here is only
         the junk ceiling). With a usable headline the item is built in the
         poster order: highlight, headline, subline, timer, cta. Without one
         the row renders exactly as it did before `bar` existed. Never a code
         element and never a price: this is a public advertisement. */
      var bar = plainObject(r.bar);
      var barHead = bar ? publicText(bar.headline, 240) : null;
      if (barHead) {
        var typed = [];
        if (typeof r.highlight === 'number' && isFinite(r.highlight) && r.highlight > 0 && r.highlight <= 100) {
          typed.push({ type: 'highlight', pct: formatHighlight(r.highlight) });
        }
        typed.push({ type: 'headline', text: barHead });
        var barSub = publicText(bar.subline, 240);
        if (barSub) typed.push({ type: 'subline', text: barSub, optional: true });
        /* no label key means the server-rendered default label stays */
        var barTimer = { type: 'timer' };
        var timerLabel = publicText(bar.timer_label, 240);
        if (timerLabel) barTimer.label = timerLabel;
        typed.push(barTimer);
        /* NO DESTINATION MEANS NO CTA, for anyone - and the item still
           stands. With one, a visitor looking at a members campaign is sent
           to JOIN (the offer is not theirs to open yet); a member, or anyone
           on a public campaign, gets the campaign's own link under the
           server's label. A missing join link or a missing label drops the
           button alone, never the item. */
        if (path) {
          if (r.audience !== 'public' && !Store.read()) {
            if (joinLink) typed.push({ type: 'cta', label: joinCta, href: joinLink, optional: true });
          } else {
            var ctaLabel = publicText(bar.cta_label, 240);
            if (ctaLabel) typed.push({ type: 'cta', label: ctaLabel, href: path, optional: true });
          }
        }
        /* the island owns both gates: an unknown token degrades to the
           default treatment, and safeArt admits the store's own CDN only */
        if (typeof bar.accent === 'string' && bar.accent !== '') {
          typed.push({ type: 'accent', token: bar.accent, optional: true });
        }
        if (typeof bar.art_src === 'string' && bar.art_src !== '') {
          typed.push({ type: 'art', src: bar.art_src, optional: true });
        }
        items.push({ id: 'drops:public:' + r.campaign_id, source: 'drops_public',
          trigger: r.campaign_id, band: 'campaign', elements: typed,
          a11y_label: barHead + (barSub ? '. ' + barSub : ''),
          window: { start_at: r.window_start_at, end_at: r.window_end_at } });
        return;
      }
      var elements = [{ type: 'headline', text: headline }];
      if (typeof r.highlight === 'number' && isFinite(r.highlight) && r.highlight > 0 && r.highlight <= 100) {
        elements.push({ type: 'highlight', pct: formatHighlight(r.highlight) });
      }
      elements.push({ type: 'timer' });
      if (path) elements.push({ type: 'cta', label: en ? 'View offer' : 'לצפייה בהצעה', href: path, optional: false });
      items.push({ id: 'drops:public:' + r.campaign_id, source: 'drops_public',
        trigger: r.campaign_id, band: 'campaign', elements: elements,
        a11y_label: headline,
        window: { start_at: r.window_start_at, end_at: r.window_end_at } });
    });
    var joinEnd = toMs(value.join_expires_at);
    if (typeof value.join_url === 'string' && PUBLIC_JOIN.test(value.join_url) && joinEnd !== null && joinEnd > now) {
      var joinHref = (JOIN_URL && PUBLIC_JOIN.test(JOIN_URL)) ? JOIN_URL : value.join_url;
      /* the poster bar's join card (owner copy 2026-09-21): the club's own
         colour, the welcome offer stated — 10% off the first order (the
         owner-settled welcome gift) plus the member-only offers. Language
         follows <html lang>: Hebrew store, English on /en. The supporting
         line and the accent are optional so an older bar still shows the
         headline and the button. */
      var label = en ? 'Join EXON Drop' : 'מצטרפים ל-EXON Drop';
      var sub = en ? '10% off your first order, plus member-only offers on Telegram.' : '10% הנחה על ההזמנה הראשונה, ועוד הצעות בלעדיות לחברים בטלגרם.';
      items.push({ id: 'drops:public:join', source: 'drops_public', trigger: null,
        band: 'campaign', elements: [
          { type: 'highlight', pct: 'DROP' },
          { type: 'headline', text: label },
          { type: 'subline', text: sub, optional: true },
          { type: 'cta', label: joinCta, href: joinHref, optional: false },
          { type: 'accent', token: 'magenta', optional: true }
        ], a11y_label: label + '. ' + sub, window: { start_at: null, end_at: value.join_expires_at } });
    }
    return items;
  }
  function schedulePublic() {
    if (publicTimer) clearTimeout(publicTimer);
    publicTimer = null;
    if (!syncVisible() || !publicSurface()) return;
    publicTimer = setTimeout(function () { publicTimer = null; refreshPublic(); },
      Math.max(60000 + Math.random() * 30000, publicAfter - Date.now()));
  }
  function refreshPublic() {
    if (publicTimer) clearTimeout(publicTimer);
    publicTimer = null;
    if (!syncVisible() || !publicSurface()) return;
    if (publicInFlight) return publicInFlight;
    if (Date.now() < publicAfter) { if (publicHealthy) schedulePublic(); return; }
    publicAfter = Date.now() + 60000;
    var preview = readPreview();
    publicInFlight = post('/public', preview ? { preview: preview } : {}, null).then(function (body) {
      replacePublic(publicItems(body));
      publicHealthy = true;
      schedulePublic();
    }, function (err) {
      // An outage withdraws only public advertisements. No retry loop and no
      // inferred loss of private membership or benefits.
      replacePublic([]);
      publicHealthy = false;
      publicAfter = Date.now() + Math.max(60000 + Math.random() * 30000,
        (err.retryAfterSeconds || 0) * 1000);
    }).finally(function () { publicInFlight = null; publicFirstEnded(); });
    return publicInFlight;
  }
  /* the first read ended (answer, error or timeout alike; run from finally,
     so AFTER its items were registered, and even if a handler threw): the
     island may now settle. Said once; later reads are silent. */
  function publicFirstEnded() {
    if (!publicFirstPending) return;
    publicFirstPending = false;
    dropsApi.publicPending = false;
    try {
      document.dispatchEvent(new CustomEvent('exon:drops:public'));
    } catch (e) { /* no consumer is load-bearing */ }
  }
  function installPublic() {
    if (publicInstalled || !ENABLED || !publicSurface() || typeof document.addEventListener !== 'function') return;
    publicInstalled = true;
    document.addEventListener('visibilitychange', refreshPublic);
    if (typeof window.addEventListener === 'function') {
      window.addEventListener('pageshow', refreshPublic);
      window.addEventListener('focus', refreshPublic);
    }
    publicFirstPending = true;
    dropsApi.publicPending = true;
    refreshPublic();
  }

  function boot() {
    if (!ENABLED) return finish(NOT_ENTITLED);
    installPublic();
    var token = readExd();
    var stored = Store.read();
    if (token) return exchange(token, stored);
    if (stored) return refresh(stored);
    return finish(NOT_ENTITLED);
  }

  try {
    boot();
  } catch (e) {
    /* a synchronous throw here means the client never ran at all, which the
       customer cannot see and an operator must */
    fault('boot_threw', e);
    finish(NOT_ENTITLED);
  }
})();
